import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { PincodeInput } from '../components/PincodeInput';
import { FileText, CheckCircle, Upload, Key, Store, BadgeCheck, RefreshCw, AlertTriangle, Shield, Package, Trash2, Loader2, CreditCard, FileCheck, Building2, Landmark, Eye, Download, ArrowLeft, Edit, DollarSign, Mail, MapPin, Phone, Save, X, MoreVertical, UserCheck, UserX, Ban, XCircle, Database, History } from 'lucide-react';

// Full document types matching vendor Profile.jsx KYC section
const HOLIDAY_REASON_LABELS = {
  vendor_requested: 'Vendor Requested',
  trial_expired: 'Trial Expired',
  subscription_expired: 'Subscription Expired',
  payment_failure: 'Payment Failure',
  admin_suspended: 'Admin Suspended',
};
const ADMIN_DOCUMENT_TYPES = [
  { id: 'pan_card', name: 'PAN Card', description: 'Permanent Account Number card', required: true, icon: CreditCard, legacyKeys: ['pan', 'pan_card'] },
  { id: 'aadhaar_card', name: 'Aadhaar Card', description: 'Government issued identity proof', required: true, icon: FileCheck, legacyKeys: ['aadhaar_card'] },
  { id: 'gst_certificate', name: 'GST Certificate', description: 'GST registration certificate', required: false, icon: FileText, legacyKeys: ['gst', 'gst_certificate'] },
  { id: 'msme_certificate', name: 'MSME Certificate', description: 'Udyam registration', required: false, icon: Building2, legacyKeys: ['msme_certificate'] },
  { id: 'bank_statement', name: 'Bank Statement', description: 'Last 3 months bank statement', required: false, icon: Landmark, legacyKeys: ['bank_statement', 'bank'] },
  { id: 'cancelled_cheque', name: 'Cancelled Cheque', description: 'For bank account verification', required: true, icon: CreditCard, legacyKeys: ['cancelled_cheque'] },
  { id: 'business_license', name: 'Business License', description: 'Shop & Establishment Act or Trade License', required: false, icon: Shield, legacyKeys: ['business_license'] },
  { id: 'address_proof', name: 'Address Proof', description: 'Utility bill, rent agreement, or property docs', required: false, icon: FileText, legacyKeys: ['address_proof'] },
  { id: 'brand_authority_letter', name: 'Brand Authority Letter', description: 'Authorization from brand owner', required: false, icon: FileCheck, legacyKeys: ['brand_authority_letter'] },
  { id: 'gst_invoices_3months', name: '3 Months GST Invoices', description: 'GST invoices from distributor', required: false, icon: FileText, legacyKeys: ['gst_invoices_3months'] },
  { id: 'trademark_certificate', name: 'Trademark Certificate', description: 'Registered trademark certificate', required: false, icon: Shield, legacyKeys: ['trademark_certificate'] },
  { id: 'fssai_certificate', name: 'FSSAI Certificate', description: 'Food Safety license', required: false, icon: FileCheck, legacyKeys: ['fssai_certificate'] },
  { id: 'shop_establishment', name: 'Shop & Establishment', description: 'Registration certificate', required: false, icon: Building2, legacyKeys: ['shop_establishment'] },
  { id: 'trade_license', name: 'Trade License', description: 'Trade license by local authority', required: false, icon: Shield, legacyKeys: ['trade_license'] },
  { id: 'tan_document', name: 'TAN Document', description: 'Tax Deduction and Collection Number', required: false, icon: CreditCard, legacyKeys: ['tan_document'] },
  { id: 'iec_license', name: 'IEC License', description: 'Import Export Code license', required: false, icon: FileText, legacyKeys: ['iec_license'] },
];
import VendorCredentialsTab from '../components/admin/VendorCredentialsTab';
import RazorpayRouteDiagnoseCard from '../components/admin/RazorpayRouteDiagnoseCard';
import { StoreWideLicensesCard } from '../components/vendor/StoreWideLicensesCard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const VendorDetailsNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall } = useAuth();
  
  const [vendor, setVendor] = useState(null);
  // iter-233 — Full SSOT-resolved subscription detail fetched on demand
  // for the Subscription tab (the bulk vendor doc doesn't include
  // plan/usage/trial-extension state).
  const [adminSubData, setAdminSubData] = useState(null);
  const [adminSubLoading, setAdminSubLoading] = useState(false);
  const [subHistory, setSubHistory] = useState([]);
  const [subHistoryLoading, setSubHistoryLoading] = useState(false);

  // Single-vendor plan change
  const PLAN_OPTIONS = [
    { code: 'launch',       label: 'Launch',       commission: 18 },
    { code: 'basic',        label: 'Basic',         commission: 15 },
    { code: 'professional', label: 'Professional', commission: 12 },
    { code: 'business',     label: 'Business',     commission: 10 },
    { code: 'enterprise',   label: 'Enterprise',   commission:  8 },
  ];
  const [planDialog,      setPlanDialog]      = useState(false);
  const [planDialogCode,  setPlanDialogCode]  = useState('');
  const [planDialogReason,setPlanDialogReason]= useState('');
  const [planSaving,      setPlanSaving]      = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [editMode, setEditMode] = useState(false);
  const [commissionEditMode, setCommissionEditMode] = useState(false);
  const [kycDialog, setKycDialog] = useState({ open: false, type: '', url: '' });
  const [uploadingKyc, setUploadingKyc] = useState(null);

  // iter-374b — Admin view/edit of the vendor's store-wide default
  // licenses (Business Info tab). Read-only by default; admin toggles
  // Edit explicitly. Every save writes an admin_audit_logs entry.
  const [vendorLicenses, setVendorLicenses] = useState([]);
  const [licensesEditing, setLicensesEditing] = useState(false);
  const [licensesSaving, setLicensesSaving] = useState(false);
  const [uploadingLicenseIdx, setUploadingLicenseIdx] = useState(null);
  const [verifyingLicenseIdx, setVerifyingLicenseIdx] = useState(null);
  const [licensesLastEdited, setLicensesLastEdited] = useState(null);
  
  const [editData, setEditData] = useState({
    name: '',
    business_name: '',
    legal_name: '',
    mobile: '',
    support_email: '',
    support_mobile: '',
    business_address: '',
    city: '',
    state: '',
    pincode: '',
    gst_number: '',
    pan_number: '',
    // iter-233-F — Personal PAN (4th char=P, individual) + IEC code.
    personal_pan: '',
    iec_code: '',
    enrollment_id: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    account_holder_name: '',
    company_type: '',
    annual_turnover: '',
    business_type: ''
  });
  
  const [newCommission, setNewCommission] = useState('');
  
  // Rename Store Dialog State
  const [renameDialog, setRenameDialog] = useState({
    open: false,
    newStoreName: '',
    reason: '',
    correctionType: 'correction', // 'correction' or 'brand_change'
    sendNotification: true,
    creditAdAmount: 0,
    submitting: false
  });
  
  // Manual Pricing State
  const [manualPricingEnabled, setManualPricingEnabled] = useState(false);
  const [manualPricingMessage, setManualPricingMessage] = useState('');
  const [savingManualPricing, setSavingManualPricing] = useState(false);
  
  // Delete Vendor State
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    confirmText: '',
    deleting: false
  });
  
  // Tier Edit State
  const [tierEditing, setTierEditing] = useState(false);
  const [savingTier, setSavingTier] = useState(false);
  const [verifyingGst, setVerifyingGst] = useState(false);
  
  // GST CAPTCHA Modal state — real portal verification (admin flow)
  const [gstModal, setGstModal] = useState({
    open: false,
    loading: false,
    sessionId: '',
    captchaImage: '',
    captchaAnswer: '',
    submitting: false,
    error: ''
  });
  
  // GST Portal Data State
  const [gstPortalData, setGstPortalData] = useState(null);
  const [gstPortalLoading, setGstPortalLoading] = useState(false);

  // WhatsApp opt-in toggle (Store Identity tab — iter-254)
  const [whatsappOptinSaving, setWhatsappOptinSaving] = useState(false);

  const handleToggleWhatsappOptin = async (nextValue) => {
    setWhatsappOptinSaving(true);
    try {
      await apiCall(`/api/vendors/${id}/whatsapp-optin`, {
        method: 'PUT',
        body: JSON.stringify({ opted_in: nextValue }),
      });
      toast({
        title: nextValue ? 'WhatsApp Opted In' : 'WhatsApp Opted Out',
        description: nextValue
          ? 'Vendor will now receive new order alerts on WhatsApp.'
          : 'Vendor will no longer receive WhatsApp alerts.',
      });
      fetchVendorDetails({ silent: true });
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'Failed to update WhatsApp opt-in', variant: 'destructive' });
    } finally {
      setWhatsappOptinSaving(false);
    }
  };

  // iter-325 — admin stopgap: mark Naming Policy accepted + unblock the
  // vendor's dashboard access without re-running/duplicating any
  // registration data (see routes/admin_vendor_diagnostics.py).
  const [forceUnblockSaving, setForceUnblockSaving] = useState(false);
  const handleForceCompleteOnboarding = async () => {
    setForceUnblockSaving(true);
    try {
      await apiCall(`/api/admin/vendor-diagnostics/${id}/force-complete-onboarding`, {
        method: 'POST',
        body: JSON.stringify({ accept_naming_policy: true, reason: 'Admin verified profile is complete — manual unblock' }),
      });
      toast({ title: 'Vendor Unblocked', description: 'Naming Policy marked accepted and dashboard access restored.' });
      fetchVendorDetails({ silent: true });
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'Failed to unblock vendor', variant: 'destructive' });
    } finally {
      setForceUnblockSaving(false);
    }
  };

  // iter-325 — Naming Policy toggle (Store Identity tab) — updates the
  // existing vendor record directly, does NOT touch store_name/slug and
  // never re-triggers the registration flow.
  const [namingPolicySaving, setNamingPolicySaving] = useState(false);
  const handleToggleNamingPolicy = async (nextValue) => {
    setNamingPolicySaving(true);
    try {
      await apiCall(`/api/vendors/${id}/naming-policy`, {
        method: 'PUT',
        body: JSON.stringify({ accepted: nextValue }),
      });
      toast({
        title: nextValue ? 'Naming Policy Accepted' : 'Naming Policy Un-accepted',
        description: nextValue
          ? 'Marked accepted on the vendor\'s behalf.'
          : 'Marked as not accepted.',
      });
      fetchVendorDetails({ silent: true });
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'Failed to update Naming Policy status', variant: 'destructive' });
    } finally {
      setNamingPolicySaving(false);
    }
  };

  // iter-329 — Onboarding artifact verdict (the ACTUAL gate that
  // decides whether a vendor keeps looping onto onboarding routes —
  // independent of, and can override, `onboarding_completed`). See
  // utils/onboarding_guard.py::compute_onboarding_artifacts.
  const [onboardingArtifacts, setOnboardingArtifacts] = useState(null);
  const [overrideSaving, setOverrideSaving] = useState(false);
  const fetchOnboardingArtifacts = async () => {
    try {
      const diag = await apiCall(`/api/admin/vendor-diagnostics/${id}`);
      setOnboardingArtifacts(diag.onboarding_artifacts || null);
    } catch (e) {
      // Non-fatal — the checklist card just won't render its detail.
    }
  };
  const handleToggleOnboardingOverride = async (enable) => {
    setOverrideSaving(true);
    try {
      const resp = await apiCall(`/api/admin/vendor-diagnostics/${id}/override-onboarding-gate`, {
        method: 'POST',
        body: JSON.stringify({
          enable,
          reason: enable
            ? 'Admin override — vendor verified complete but stuck on artifact-driven onboarding loop'
            : 'Admin cleared onboarding gate override',
        }),
      });
      setOnboardingArtifacts(resp.onboarding_artifacts || null);
      toast({
        title: enable ? 'Onboarding Gate Overridden' : 'Override Cleared',
        description: enable
          ? 'Vendor will now land on their dashboard regardless of any missing onboarding artifact.'
          : 'Vendor is back under the normal artifact-driven onboarding gate.',
      });
      fetchVendorDetails({ silent: true });
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'Failed to update onboarding override', variant: 'destructive' });
    } finally {
      setOverrideSaving(false);
    }
  };

  const fetchVendorDetails = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const data = await apiCall(`/api/vendors/${id}`);
      setVendor(data);
      
      // Populate edit data
      const vp = data.vendor_profile || {};
      const bd = vp.business_details || {};
      const cd = vp.contact_details || {};
      const bk = vp.bank_details || {};
      
      setEditData({
        name: data.name || '',
        business_name: bd.business_name || '',
        legal_name: data.identity?.legal_name || bd.legal_name || '',
        mobile: cd.mobile || '',
        support_email: cd.support_email || '',
        support_mobile: cd.support_mobile || '',
        business_address: bd.business_address || '',
        city: bd.city || '',
        state: bd.state || '',
        pincode: bd.pincode || '',
        gst_number: bd.gst_number || '',
        pan_number: bd.pan_number || '',
        // iter-233-F — Personal PAN hydrates from contact_details.pan
        // (mirror written by tax-registration save). IEC from
        // business_details.iec_code.
        personal_pan: cd.pan || data.personal_pan || '',
        iec_code: bd.iec_code || '',
        enrollment_id: bd.enrollment_id || '',
        bank_name: bk.bank_name || '',
        account_number: bk.account_number || '',
        ifsc_code: bk.ifsc_code || '',
        account_holder_name: bk.account_holder_name || '',
        company_type: bd.company_type || '',
        annual_turnover: bd.annual_turnover || '',
        business_type: bd.business_type || ''
      });
      
      setNewCommission(vp.commission?.rate || '10');
      
      // Check for manual pricing override for this vendor
      try {
        const pricingConfig = await apiCall(`/api/product-option-templates/system/pricing-config?vendor_id=${id}`);
        if (pricingConfig.override_source === 'vendor') {
          setManualPricingEnabled(true);
          setManualPricingMessage(pricingConfig.message || '');
        }
      } catch (e) {
        // Ignore pricing config fetch errors
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load vendor details',
        variant: 'destructive'
      });
      navigate('/vendors');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchVendorLicenses = async () => {
    try {
      const res = await apiCall(`/api/vendors/${id}/licenses`);
      setVendorLicenses(res?.licenses || []);
      setLicensesLastEdited(
        res?.last_edited_by ? { by: res.last_edited_by, at: res.last_edited_at } : null
      );
    } catch (e) { /* non-critical */ }
  };

  const updateVendorLicense = (idx, patch) => {
    setVendorLicenses((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const addVendorLicenseRow = () => {
    setVendorLicenses((prev) => [...prev, { type: 'FSSAI', number: '', image_url: '' }]);
  };

  const removeVendorLicenseRow = (idx) => {
    setVendorLicenses((prev) => prev.filter((_, i) => i !== idx));
  };

  const uploadVendorLicenseImage = async (idx, file) => {
    setUploadingLicenseIdx(idx);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const token = sessionStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/media/upload?optimize=false`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: uploadFormData,
      });
      if (response.ok) {
        const data = await response.json();
        updateVendorLicense(idx, { image_url: data.cdn_url || data.file_url || data.url });
      }
    } finally {
      setUploadingLicenseIdx(null);
    }
  };

  const handleVerifyLicense = async (idx, renewalConfirmed = false) => {
    setVerifyingLicenseIdx(idx);
    try {
      const res = await apiCall(`/api/vendors/${id}/licenses/${idx}/verify`, {
        method: 'POST',
        body: JSON.stringify({ renewal_confirmed: renewalConfirmed }),
      });
      if (res.license) updateVendorLicense(idx, res.license);
      switch (res.outcome) {
        case 'verified':
          toast({ title: 'Verified', description: `FSSAI license verified — expires ${res.license?.expiry_date || 'N/A'}.` });
          break;
        case 'already_verified':
          toast({ title: 'Already Verified', description: res.message || 'This FSSAI license is already verified — no need to re-verify.' });
          break;
        case 'needs_confirmation':
          toast({ title: 'Confirm Renewal', description: res.message || 'Please confirm the vendor has renewed this license, then re-verify.' });
          break;
        case 'blocked_attempts':
          toast({ title: 'Verification Limit Reached', description: res.message, variant: 'destructive' });
          break;
        case 'blocked_concurrent':
          toast({ title: 'Please Wait', description: res.message, variant: 'destructive' });
          break;
        case 'technical_failure':
          toast({ title: 'Verifico Unavailable', description: res.message || 'Verification service is temporarily unavailable — this attempt was not counted. Please try again shortly.', variant: 'destructive' });
          break;
        default:
          toast({ title: 'Verification Failed', description: res.message || res.license?.last_verification_error || 'Could not verify this license.', variant: 'destructive' });
      }
    } catch (error) {
      const msg = error.message || '';
      if (msg.toLowerCase().includes('out of range')) {
        toast({ title: 'Save Required', description: 'Please click "Save Licenses" first, then verify this entry.', variant: 'destructive' });
      } else {
        toast({ title: 'Error', description: msg || 'Failed to verify license', variant: 'destructive' });
      }
    } finally {
      setVerifyingLicenseIdx(null);
    }
  };

  const handleSaveVendorLicenses = async () => {
    setLicensesSaving(true);
    try {
      const res = await apiCall(`/api/vendors/${id}/licenses`, {
        method: 'PUT',
        body: JSON.stringify({
          licenses: vendorLicenses.map((l) => ({ type: l.type, number: l.number, image_url: l.image_url })),
        }),
      });
      setVendorLicenses(res?.licenses || []);
      setLicensesLastEdited(res?.updated_by ? { by: res.updated_by, at: res.updated_at } : null);
      setLicensesEditing(false);
      toast({
        title: 'Success',
        description: `Licenses updated. ${res?.listings_resynced || 0} listing(s) auto-synced.`,
      });
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to update licenses', variant: 'destructive' });
    } finally {
      setLicensesSaving(false);
    }
  };

  useEffect(() => {
    fetchVendorDetails();
    fetchOnboardingArtifacts();
    fetchVendorLicenses();
  }, [id]); // fetchVendorDetails uses id internally

  // iter-233 — Lazily fetch the SSOT-resolved subscription + plan + usage
  // for the Subscription tab. Powered by
  // GET /api/vendor-subscription/admin/vendor/{id}/subscription which
  // returns the same `{resolved, subscription, plan, usage}` shape the
  // vendor sees on their own SubscriptionHub.
  const fetchAdminSubscription = async () => {
    if (!id || adminSubLoading) return;
    setAdminSubLoading(true);
    try {
      const data = await apiCall(`/api/vendor-subscription/admin/vendor/${id}/subscription`);
      setAdminSubData(data);
    } catch (e) {
      console.warn('[admin-sub] fetch failed', e);
    } finally {
      setAdminSubLoading(false);
    }
  };

  const fetchSubHistory = async () => {
    if (!id || subHistoryLoading) return;
    setSubHistoryLoading(true);
    try {
      const data = await apiCall(`/api/admin/vendor-subscriptions/${id}/history`);
      setSubHistory(data?.records || []);
    } catch (e) {
      console.warn('[sub-history] fetch failed', e);
    } finally {
      setSubHistoryLoading(false);
    }
  };

  const handleSinglePlanChange = async () => {
    if (!planDialogCode) return;
    setPlanSaving(true);
    try {
      await apiCall('/api/vendors/bulk-change-plan', {
        method: 'PUT',
        body: JSON.stringify({
          vendor_ids: [id],
          plan_code: planDialogCode,
          reason: planDialogReason || 'admin_single_plan_change',
        }),
      });
      const plan = PLAN_OPTIONS.find(p => p.code === planDialogCode);
      toast({ title: 'Plan Updated', description: `Changed to ${plan?.label} (${plan?.commission}%)` });
      setPlanDialog(false);
      setPlanDialogCode('');
      setPlanDialogReason('');
      fetchAdminSubscription();
      fetchSubHistory();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'Failed to change plan', variant: 'destructive' });
    } finally {
      setPlanSaving(false);
    }
  };

  // Phase 7 — Load subscription data alongside vendor details so the
  // Commission Rate widget on the Details tab always shows the SSOT value.
  useEffect(() => {
    if (id) fetchAdminSubscription();
  }, [id]); // fetchAdminSubscription is stable; intentional omission

  const fetchGstPortalData = async () => {
    setGstPortalLoading(true);
    try {
      const data = await apiCall(`/api/vendors/${id}/gst-portal-data`);
      setGstPortalData(data);
    } catch (error) {
      console.error('Failed to fetch GST portal data:', error);
    } finally {
      setGstPortalLoading(false);
    }
  };

  const handleActivateDeactivate = async () => {
    const endpoint = vendor.status === 'active' 
      ? `/api/vendors/${id}/deactivate`
      : `/api/vendors/${id}/activate`;
    try {
      await apiCall(endpoint, { method: 'PUT' });
      toast({ title: 'Success', description: `Vendor ${vendor.status === 'active' ? 'deactivated' : 'activated'}` });
      fetchVendorDetails();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleKycApprove = async () => {
    const vr = vendor.vendor_profile?.verifications || {};
    const newStatus = !vr.gst_verified;
    if (!window.confirm(newStatus ? 'Approve KYC verification?' : 'Revoke KYC verification?')) return;
    try {
      await apiCall(`/api/vendors/${id}/verify-kyc`, {
        method: 'PUT',
        body: JSON.stringify({ gst_verified: newStatus })
      });
      toast({ title: newStatus ? 'GST Verified' : 'GST Unverified', description: newStatus ? 'GST marked as verified' : 'GST verification removed' });
      fetchVendorDetails();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    // Build the exact payload the server will receive
    const payload = {
      name: editData.name,
      business_details: {
        business_name: editData.business_name,
        legal_name: editData.legal_name,
        gst_number: editData.gst_number,
        pan_number: editData.pan_number,
        // iter-233-F — IEC code lives on Business Details.
        iec_code: editData.iec_code,
        enrollment_id: editData.enrollment_id,
        business_address: editData.business_address,
        city: editData.city,
        state: editData.state,
        pincode: editData.pincode,
        company_type: editData.company_type,
        annual_turnover: editData.annual_turnover,
        business_type: editData.business_type,
      },
      contact_details: {
        mobile: editData.mobile,
        support_email: editData.support_email,
        support_mobile: editData.support_mobile,
        // iter-233-F — Personal PAN mirror so Razorpay activation
        // orchestrator (iter-233-E) picks it up first.
        pan: editData.personal_pan,
        mobile_verified: vendor?.vendor_profile?.contact_details?.mobile_verified || false
      },
      bank_details: {
        bank_name: editData.bank_name,
        account_number: editData.account_number,
        ifsc_code: editData.ifsc_code,
        account_holder_name: editData.account_holder_name,
        verified: vendor?.vendor_profile?.bank_details?.verified || false
      }
    };
    console.log('[VendorEdit] Saving payload:', payload);
    try {
      const result = await apiCall(`/api/vendors/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      console.log('[VendorEdit] Server responded:', result);
      toast({ title: 'Saved ✓', description: 'Vendor details updated.' });
      setEditMode(false);
      // Silent refetch: do NOT toggle `loading` — that would unmount <Tabs>
      // and reset the active tab back to "details" (see bug fix for tab reset issue).
      await fetchVendorDetails({ silent: true });
    } catch (error) {
      const msg = String(error?.message || error || '');
      console.error('[VendorEdit] Save failed:', error, 'msg:', msg);
      let friendly = msg || 'Unknown error';
      if (msg.includes('401') || msg.includes('unauthor') || msg.includes('Session expired')) {
        friendly = 'Your admin session has expired. Please log in again and retry.';
      } else if (msg.includes('403') || msg.includes('forbidden') || msg.includes('permission')) {
        friendly = 'You do not have permission to edit this vendor.';
      } else if (msg.includes('422') || msg.includes('validation')) {
        friendly = `Validation error: ${msg}. Check GSTIN format (15 chars) or PAN format (10 chars).`;
      } else if (msg.includes('Failed to fetch') || msg.includes('network') || msg.includes('Network')) {
        friendly = 'Network error — please check your connection and retry.';
      }
      toast({
        title: 'Save failed',
        description: friendly,
        variant: 'destructive'
      });
    }
  };

  const updateCommission = async () => {
    const rate = parseFloat(newCommission);
    
    // Validate range
    if (rate < 5 || rate > 20) {
      toast({
        title: 'Invalid Commission',
        description: 'Commission rate must be between 5% and 20%',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      await apiCall(`/api/vendors/${id}/commission?commission_rate=${rate}`, {
        method: 'PUT'
      });
      
      toast({ title: 'Success', description: 'Commission updated successfully' });
      setCommissionEditMode(false);
      fetchVendorDetails();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  // Update vendor tier manually
  const updateVendorTier = async (newTier) => {
    setSavingTier(true);
    try {
      await apiCall(`/api/vendors/${id}/tier`, {
        method: 'PUT',
        body: JSON.stringify({ tier: newTier })
      });
      toast({ title: 'Tier Updated', description: `Vendor tier set to ${TIER_MAP[newTier] || newTier}` });
      setTierEditing(false);
      fetchVendorDetails();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSavingTier(false);
    }
  };

  // Toggle GST verification status (uses dedicated admin endpoint)
  const toggleGstVerification = async () => {
    const vr = vendor?.vendor_profile?.verifications || {};
    // If currently verified → admin can unverify via old kyc toggle (fast path, no CAPTCHA).
    if (vr.gst_verified) {
      setVerifyingGst(true);
      try {
        await apiCall(`/api/vendors/${id}/verify-kyc`, {
          method: 'PUT',
          body: JSON.stringify({ gst_verified: false })
        });
        toast({ title: 'GST Unverified', description: 'Verification removed' });
        fetchVendorDetails();
      } catch (error) {
        toast({ title: 'Error', description: error.message, variant: 'destructive' });
      } finally {
        setVerifyingGst(false);
      }
      return;
    }
    // If NOT verified → open CAPTCHA modal and hit the real GST portal
    const bdNow = vendor?.vendor_profile?.business_details || {};
    const gstinNow = (editData.gst_number || bdNow.gst_number || '').trim().toUpperCase();
    if (!gstinNow || gstinNow.length !== 15) {
      toast({ title: 'Enter GSTIN', description: 'Please enter the 15-character GSTIN first.', variant: 'destructive' });
      return;
    }
    await openGstCaptchaModal(gstinNow);
  };

  const openGstCaptchaModal = async (gstin) => {
    setGstModal({ open: true, loading: true, sessionId: '', captchaImage: '', captchaAnswer: '', submitting: false, error: '', gstin });
    try {
      const data = await apiCall('/api/gst-verification/captcha');
      setGstModal(s => ({ ...s, loading: false, sessionId: data.session_id, captchaImage: data.captcha_image }));
    } catch (error) {
      setGstModal(s => ({ ...s, loading: false, error: error.message || 'Failed to load CAPTCHA' }));
    }
  };

  const refreshCaptcha = async () => {
    setGstModal(s => ({ ...s, loading: true, captchaAnswer: '', error: '' }));
    try {
      const data = await apiCall('/api/gst-verification/captcha');
      setGstModal(s => ({ ...s, loading: false, sessionId: data.session_id, captchaImage: data.captcha_image }));
    } catch (error) {
      setGstModal(s => ({ ...s, loading: false, error: error.message || 'Failed to load CAPTCHA' }));
    }
  };

  const submitGstCaptcha = async () => {
    if (!gstModal.captchaAnswer.trim()) {
      setGstModal(s => ({ ...s, error: 'Enter the CAPTCHA text' }));
      return;
    }
    setGstModal(s => ({ ...s, submitting: true, error: '' }));
    try {
      const result = await apiCall('/api/gst-verification/admin/verify-vendor', {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: id,
          session_id: gstModal.sessionId,
          gstin: (gstModal.gstin || editData.gst_number || bd.gst_number || '').toUpperCase(),
          captcha: gstModal.captchaAnswer.trim()
        })
      });
      toast({
        title: 'GST Verified ✓',
        description: `Legal name: ${result?.details?.legal_name || 'fetched from GST portal'}`
      });
      setGstModal({ open: false, loading: false, sessionId: '', captchaImage: '', captchaAnswer: '', submitting: false, error: '' });
      fetchVendorDetails();
      if (typeof fetchGstPortalData === 'function') fetchGstPortalData();
    } catch (error) {
      setGstModal(s => ({ ...s, submitting: false, error: error.message || 'Verification failed. Try again with a fresh CAPTCHA.' }));
    }
  };

  const viewDocument = (type) => {
    const kycDocs = vp.kyc_documents || {};
    // Find the document type config for legacy key resolution
    const docConfig = ADMIN_DOCUMENT_TYPES.find(d => d.id === type || (d.legacyKeys && d.legacyKeys.includes(type)));
    const keysToTry = docConfig ? docConfig.legacyKeys : [type];
    let documentUrl = null;
    for (const key of keysToTry) {
      if (kycDocs[key]) {
        documentUrl = kycDocs[key];
        break;
      }
    }
    
    if (!documentUrl) {
      toast({
        title: 'No Document',
        description: `No ${type.replace(/_/g, ' ')} document uploaded yet`,
        variant: 'destructive'
      });
      return;
    }

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    // iter-229g — Detect legacy local-disk URLs that point to files
    // that no longer exist (wiped on K8s redeploy). Return null so the
    // UI shows a clear "missing — please re-upload" state instead of
    // attempting a 404'd image fetch.
    const isLostLegacyKycUrl = (url) => {
      if (!url || typeof url !== 'string') return false;
      const v = url.trim();
      if (v.startsWith('/api/uploads/')) return true;
      if (v.startsWith('/uploads/')) return true;
      if (!v.includes('://') && !v.startsWith('/') && !v.includes('/')) return true;
      return false;
    };
    const resolveUrl = (url) => {
      if (!url) return null;
      if (isLostLegacyKycUrl(url)) return null;
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
      return null;
    };
    const fullUrl = resolveUrl(documentUrl);
    // iter-229g — If the resolver returned null but we DID have a stored
    // URL, the doc was lost (legacy ephemeral storage). Show a clear
    // call-to-action instead of opening an empty dialog.
    if (!fullUrl && documentUrl) {
      toast({
        title: 'Document missing',
        description: `The ${type.replace(/_/g, ' ').toUpperCase()} file was lost during a past deployment (legacy storage). Please ask the vendor to re-upload, or upload it on their behalf below.`,
        variant: 'destructive',
      });
      return;
    }
    
    setKycDialog({
      open: true,
      type: type.replace(/_/g, ' ').toUpperCase(),
      url: fullUrl
    });
  };
  
  const handleKycUpload = async (type, event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      setUploadingKyc(type);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/vendors/${id}/upload-kyc?document_type=${type}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          },
          body: formData
        }
      );
      
      if (!response.ok) throw new Error('Upload failed');
      
      const result = await response.json();
      
      toast({
        title: 'Success',
        description: result.message
      });
      
      fetchVendorDetails();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to upload document',
        variant: 'destructive'
      });
    } finally {
      setUploadingKyc(null);
      event.target.value = '';
    }
  };
  
  // Open Rename Store Dialog
  const openRenameDialog = () => {
    const currentStoreName = 
      vendor?.identity?.store_name || 
      vendor?.vendor_profile?.business_details?.store_name || 
      vendor?.name || '';
    
    setRenameDialog({
      open: true,
      newStoreName: currentStoreName,
      reason: '',
      correctionType: 'correction',  // Default to quick correction
      sendNotification: true,
      creditAdAmount: 0,
      submitting: false
    });
  };
  
  // Handle Store Rename
  const handleRenameStore = async () => {
    if (!renameDialog.newStoreName || renameDialog.newStoreName.length < 3) {
      toast({
        title: 'Invalid Name',
        description: 'Store name must be at least 3 characters',
        variant: 'destructive'
      });
      return;
    }
    
    const minReasonLen = renameDialog.correctionType === 'correction' ? 5 : 10;
    if (!renameDialog.reason || renameDialog.reason.length < minReasonLen) {
      toast({
        title: 'Reason Required',
        description: `Please provide a reason (at least ${minReasonLen} characters)`,
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setRenameDialog(prev => ({ ...prev, submitting: true }));
      
      await apiCall('/api/brand-registry/admin/rename-store', {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: id,
          new_store_name: renameDialog.newStoreName,
          reason: renameDialog.reason,
          correction_type: renameDialog.correctionType,
          send_notification: renameDialog.sendNotification,
          credit_ad_amount: renameDialog.correctionType === 'correction' ? 0 : renameDialog.creditAdAmount
        })
      });
      
      const isCorrection = renameDialog.correctionType === 'correction';
      toast({
        title: isCorrection ? 'Name Corrected' : 'Store Renamed',
        description: isCorrection 
          ? 'Store name has been corrected successfully.' 
          : `Store has been renamed successfully${renameDialog.sendNotification ? '. Notification sent to vendor.' : '.'}`
      });
      
      setRenameDialog({ open: false, newStoreName: '', reason: '', correctionType: 'correction', sendNotification: true, creditAdAmount: 0, submitting: false });
      fetchVendorDetails();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to rename store',
        variant: 'destructive'
      });
      setRenameDialog(prev => ({ ...prev, submitting: false }));
    }
  };
  
  // Save Manual Pricing Override
  const saveManualPricing = async () => {
    try {
      setSavingManualPricing(true);
      
      if (manualPricingEnabled) {
        // Create/update override
        await apiCall('/api/product-option-templates/system/pricing-override', {
          method: 'POST',
          body: JSON.stringify({
            type: 'vendor',
            reference_id: id,
            manual_pricing_enabled: true,
            reason: 'Admin enabled manual pricing for vendor',
            custom_message: manualPricingMessage || null
          })
        });
      } else {
        // Delete override
        try {
          await apiCall(`/api/product-option-templates/system/pricing-override/vendor/${id}`, {
            method: 'DELETE'
          });
        } catch (e) {
          // Ignore if not found
        }
      }
      
      toast({
        title: 'Success',
        description: `Manual pricing ${manualPricingEnabled ? 'enabled' : 'disabled'} for this vendor`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update pricing mode',
        variant: 'destructive'
      });
    } finally {
      setSavingManualPricing(false);
    }
  };
  
  // Delete vendor handler
  const handleDeleteVendor = async () => {
    if (deleteDialog.confirmText !== 'DELETE') {
      toast({
        title: 'Confirmation Required',
        description: 'Please type DELETE to confirm',
        variant: 'destructive'
      });
      return;
    }
    
    setDeleteDialog(prev => ({ ...prev, deleting: true }));
    
    try {
      const result = await apiCall(`/api/vendors/${id}`, {
        method: 'DELETE'
      });
      
      toast({
        title: '✅ Vendor Deleted',
        description: result.message || 'Vendor and all associated data have been permanently removed.',
        duration: 5000
      });
      
      // Redirect to vendors list
      navigate('/vendors');
    } catch (error) {
      toast({
        title: 'Delete Failed',
        description: error.message || 'Failed to delete vendor. Please try again.',
        variant: 'destructive'
      });
      setDeleteDialog(prev => ({ ...prev, deleting: false }));
    }
  };
  
  // Format date helper
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading vendor details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!vendor) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Vendor not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const vp = vendor.vendor_profile || {};
  const bd = vp.business_details || {};

  // Resolve tier: prefer vendor_tax_registration (SSOT via tax_registration) → business_details → fallback
  const resolvedTier = vendor.tax_registration?.compliance_tier || vendor.tax_registration?.tax_compliance_tier || vendor.business?.compliance_tier || bd.tax_compliance_tier || bd.compliance_tier || 'tier_1_pan_only';

  // Full tier map matching production values
  const TIER_MAP = {
    'tier_1': 'Tier 1 — Starter',
    'tier_1_pan_only': 'Tier 1 — PAN Only',
    'tier_2': 'Tier 2 — Growth',
    'tier_2_gst_enrollment': 'Tier 2 — GST Enrollment',
    'tier_3': 'Tier 3 — Scale',
    'tier_3_full_gst': 'Tier 3 — Full GST',
    'tier_4_gst_iec': 'Tier 4 — GST + IEC',
    'tier_5_gst_exempt': 'Tier 5 — GST Exempt',
  };

  const TIER_OPTIONS = [
    { value: 'tier_1_pan_only', label: 'Tier 1 — PAN Only' },
    { value: 'tier_2_gst_enrollment', label: 'Tier 2 — GST Enrollment' },
    { value: 'tier_3_full_gst', label: 'Tier 3 — Full GST' },
    { value: 'tier_4_gst_iec', label: 'Tier 4 — GST + IEC' },
    { value: 'tier_5_gst_exempt', label: 'Tier 5 — GST Exempt' },
  ];
  const cd = vp.contact_details || {};
  const bk = vp.bank_details || {};
  const vr = vp.verifications || {};
  const identity = vendor.identity || {};
  const agreements = vendor.agreements || {};
  const shippingSettings = vendor.shipping_settings || {};
  const shippingModeRaw = shippingSettings.shipping_mode || 'aniyor_managed';
  const isSelfManagedShipping = shippingModeRaw === 'self_managed';
  const verificationBadges = vp.verification_badges || {};
  const isOnHoliday = !!vendor.is_on_holiday;
  const holidayModeLocked = !!vendor.holiday_mode_locked;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/vendors')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Vendors
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={openRenameDialog}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Rename Store
            </Button>
            {editMode ? (
              <>
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </>
            ) : (
              <Button onClick={() => setEditMode(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Vendor
              </Button>
            )}
            {/* Delete Vendor Button - Only show for rejected/suspended/test vendors */}
            {(vendor.status === 'rejected' || vendor.status === 'suspended' || vendor.status === 'pending') && (
              <Button 
                variant="destructive" 
                onClick={() => setDeleteDialog({ open: true, confirmText: '', deleting: false })}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Vendor
              </Button>
            )}
          </div>
        </div>
        
        {/* Vendor Header */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarFallback className="bg-blue-600 text-white text-2xl">
                    {vendor.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    {editMode ? (
                      <Input
                        value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        className="text-2xl font-bold h-auto"
                      />
                    ) : (
                      <>
                        <h1 className="text-2xl font-bold">{vendor.name}</h1>
                        {/* Verification Badges */}
                        {verificationBadges.is_brand_owner && (
                          <Badge className="bg-blue-600 text-white">
                            <BadgeCheck className="h-3 w-3 mr-1" />
                            Blue Tick
                          </Badge>
                        )}
                        {verificationBadges.is_legacy_store && (
                          <Badge className="bg-green-600 text-white">
                            <Store className="h-3 w-3 mr-1" />
                            Green Tick
                          </Badge>
                        )}
                      </>
                    )}
                    <Badge variant={vendor.status === 'active' ? 'default' : 'secondary'}>
                      {vendor.status}
                    </Badge>
                    {vendor.subscription?.plan_name && (
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200" data-testid="admin-vendor-plan-badge">
                        {vendor.subscription.plan_name}
                      </Badge>
                    )}
                  </div>
                  {/* Tier Management */}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">Tier:</span>
                    {tierEditing ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        {TIER_OPTIONS.map(t => (
                          <Button
                            key={t.value}
                            size="sm"
                            variant={resolvedTier === t.value ? 'default' : 'outline'}
                            className="h-6 px-2 text-xs"
                            disabled={savingTier}
                            onClick={() => updateVendorTier(t.value)}
                            data-testid={`tier-select-${t.value}`}
                          >
                            {t.label}
                          </Button>
                        ))}
                        <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => setTierEditing(false)}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="text-xs">
                          {TIER_MAP[resolvedTier] || resolvedTier}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-5 w-5 p-0"
                          onClick={() => setTierEditing(true)}
                          title="Edit vendor tier"
                          data-testid="edit-tier-btn"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        {/* iter-231-U — Razorpay Route Linked Account header badge */}
                        {(() => {
                          const laState = vendor?.ssot_razorpay_la_state
                            || (vendor?.razorpay_linked_account_id
                                ? ((vendor?.razorpay_linked_account_status || '').toLowerCase() === 'activated' ? 'active' : 'pending')
                                : 'missing');
                          const tone = laState === 'active'
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                            : laState === 'pending'
                              ? 'bg-amber-100 text-amber-800 border-amber-200'
                              : 'bg-rose-100 text-rose-800 border-rose-200';
                          const label = laState === 'active'
                            ? 'RZP LA Active'
                            : laState === 'pending'
                              ? 'RZP LA Pending'
                              : 'RZP LA Missing';
                          return (
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${tone}`}
                              data-testid={`vendor-header-razorpay-la-${laState}`}
                              title={vendor?.razorpay_linked_account_id || 'No linked account yet'}
                            >
                              {label}
                            </Badge>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    {/* Vendor ID */}
                    <p className="flex items-center gap-2 font-mono text-xs bg-gray-100 px-2 py-1 rounded w-fit">
                      <span className="text-gray-500">ID:</span>
                      {vendor.aniyor_id || vendor.id?.slice(0, 8)}
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {vendor.email}
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {cd.mobile || 'Not provided'}
                    </p>
                    {(identity.store_name || bd.store_name) && (
                      <p className="flex items-center gap-2">
                        <Store className="h-4 w-4" />
                        {identity.store_name || bd.store_name}
                      </p>
                    )}
                    {bd.business_name && (
                      <p className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {bd.business_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                {/* Phase 7 — Commission Rate: Read-only. Authority is Subscription Plan SSOT.
                    commission_percentage is from the vendor's active plan via V2 resolver.
                    To change commission, update the Subscription Plan on the Subscription tab. */}
                <p className="text-sm text-gray-600 mb-1">Commission Rate</p>
                {(() => {
                  const subPlanCommission = adminSubData?.plan?.commission_percentage
                    ?? adminSubData?.resolved?.commission_rate
                    ?? null;
                  const subPlanName = adminSubData?.plan?.plan_name
                    ?? adminSubData?.subscription?.plan_code
                    ?? null;
                  const hasSub = subPlanCommission != null;
                  return (
                    <div>
                      <p
                        className={`text-2xl font-bold ${hasSub ? 'text-green-600' : 'text-amber-500'}`}
                        data-testid="vendor-commission-rate"
                      >
                        {hasSub ? `${Number(subPlanCommission).toFixed(1)}%` : `${vp.commission?.rate || '—'}%`}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {hasSub
                          ? <>via {subPlanName || 'Subscription plan'}</>
                          : <span className="text-amber-500">No subscription — assign plan on Subscription tab</span>
                        }
                      </p>
                      <p className="text-xs text-gray-400">
                        Change via Subscription tab
                      </p>
                    </div>
                  );
                })()}
                
                {/* View Listings Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-full"
                  onClick={() => navigate(`/listings?vendor_id=${id}&vendor_name=${encodeURIComponent(vendor.name)}`)}
                >
                  <Package className="h-4 w-4 mr-2" />
                  View Listings
                </Button>
                
                {/* Action Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="mt-2 w-full" data-testid="vendor-detail-actions-btn">
                      <MoreVertical className="h-4 w-4 mr-2" />
                      Actions
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem
                      data-testid="vendor-action-kyc-approve"
                      onClick={handleKycApprove}
                      className={vr.gst_verified ? "text-orange-600" : "text-green-600"}
                    >
                      {vr.gst_verified ? <XCircle className="h-4 w-4 mr-2" /> : <UserCheck className="h-4 w-4 mr-2" />}
                      {vr.gst_verified ? 'Revoke KYC' : 'Approve KYC'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {vendor.status === 'active' ? (
                      <DropdownMenuItem
                        data-testid="vendor-action-deactivate"
                        onClick={handleActivateDeactivate}
                        className="text-red-600"
                      >
                        <UserX className="h-4 w-4 mr-2" />
                        Deactivate Vendor
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        data-testid="vendor-action-activate"
                        onClick={handleActivateDeactivate}
                        className="text-green-600"
                      >
                        <UserCheck className="h-4 w-4 mr-2" />
                        Activate Vendor
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* iter-233 — Mobile horizontal scroll. The 9-tab strip overflows
              the viewport on phones; wrap in an overflow-x-auto container
              and force TabsList to lay out inline so users can swipe
              between tabs. Desktop layout unchanged (sm:flex-wrap). */}
          <div className="overflow-x-auto -mx-1 px-1 sm:overflow-visible">
            <TabsList className="inline-flex w-max sm:w-full sm:flex-wrap">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="identity" className="flex items-center gap-1">
              <Shield className="h-3 w-3" />
              Store Identity
            </TabsTrigger>
            <TabsTrigger value="business">Business Info</TabsTrigger>
            <TabsTrigger value="bank">Bank Details</TabsTrigger>
            <TabsTrigger value="subscription" className="flex items-center gap-1"
              onClick={() => { if (!adminSubData) fetchAdminSubscription(); fetchSubHistory(); }}>
              <CreditCard className="h-3 w-3" />
              Subscription
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              Pricing
            </TabsTrigger>
            <TabsTrigger value="kyc">KYC Documents</TabsTrigger>
            <TabsTrigger value="gst_data" className="flex items-center gap-1" onClick={() => { if (!gstPortalData) fetchGstPortalData(); }}>
              <Database className="h-3 w-3" />
              GST Data
            </TabsTrigger>
            <TabsTrigger value="credentials" className="flex items-center gap-1">
              <Key className="h-3 w-3" />
              Credentials
            </TabsTrigger>
          </TabsList>
          </div>

          {/* Details Tab */}
          <TabsContent value="details">
            <Card>
              <CardHeader>
                <CardTitle>Vendor Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Contact Email</Label>
                    <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                      {vendor.email}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Mobile Number</Label>
                    {editMode ? (
                      <Input
                        value={editData.mobile}
                        onChange={(e) => setEditData({ ...editData, mobile: e.target.value })}
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {cd.mobile || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Support Email</Label>
                    {editMode ? (
                      <Input
                        value={editData.support_email}
                        onChange={(e) => setEditData({ ...editData, support_email: e.target.value })}
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {cd.support_email || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Support Mobile</Label>
                    {editMode ? (
                      <Input
                        value={editData.support_mobile}
                        onChange={(e) => setEditData({ ...editData, support_mobile: e.target.value })}
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {cd.support_mobile || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>
                  {/* iter-233-F — Personal PAN (director/proprietor's individual PAN).
                      Required for Tier-3/Tier-4 vendors so Razorpay Stakeholder kyc.pan
                      can pick it up (iter-233-E activate orchestrator search order). */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">
                      Personal PAN <span className="text-xs italic">(Director/Proprietor)</span>
                    </Label>
                    {editMode ? (
                      <>
                        <Input
                          placeholder="Individual PAN (4th char = P, e.g. ABCPD1234E)"
                          value={editData.personal_pan}
                          onChange={(e) => setEditData({ ...editData, personal_pan: e.target.value.toUpperCase() })}
                          maxLength={10}
                          data-testid="vendor-personal-pan-input"
                        />
                        {editData.personal_pan && editData.personal_pan.length === 10 && editData.personal_pan[3] !== 'P' && (
                          <p className="text-xs text-amber-700">
                            ⚠ 4th character must be 'P' (Individual). Razorpay will reject this on payout setup.
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium" data-testid="vendor-personal-pan-value">
                        {(cd.pan || vendor.personal_pan) || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Business Address</Label>
                  {editMode ? (
                    <Textarea
                      value={editData.business_address}
                      onChange={(e) => setEditData({ ...editData, business_address: e.target.value })}
                      rows={3}
                    />
                  ) : (
                    <div className="min-h-[76px] px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                      {bd.business_address || <span className="text-muted-foreground italic">Not provided</span>}
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Pincode / Zipcode</Label>
                    {editMode ? (
                      <PincodeInput
                        id="admin-pincode"
                        value={editData.pincode}
                        onChange={(v) => setEditData({ ...editData, pincode: v })}
                        onAutoFill={({ city, state }) => {
                          setEditData(prev => ({
                            ...prev,
                            ...(city ? { city } : {}),
                            ...(state ? { state } : {}),
                          }));
                        }}
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {bd.pincode || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">City</Label>
                    {editMode ? (
                      <Input
                        value={editData.city}
                        onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                        placeholder="Auto-filled from pincode"
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {bd.city || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">State</Label>
                    {editMode ? (
                      <Input
                        value={editData.state}
                        onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                        placeholder="Auto-filled from pincode"
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {bd.state || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Store Identity Tab */}
          <TabsContent value="identity">
            <div className="space-y-4">
              {/* Store Identity Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Store className="h-5 w-5" />
                    Store Identity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">
                        Legal Business Name (Private)
                        {vr.gst_verified && <Badge className="ml-2 text-xs bg-green-100 text-green-800">GST Verified – Locked</Badge>}
                      </Label>
                      {editMode && !vr.gst_verified ? (
                        <div>
                          <Input
                            value={editData.legal_name}
                            onChange={(e) => setEditData({ ...editData, legal_name: e.target.value })}
                            placeholder="Legal registered business name"
                          />
                          <p className="text-xs text-amber-600 mt-1">Editable until GST is verified</p>
                        </div>
                      ) : (
                        <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                          {identity.legal_name || bd.legal_name || <span className="text-muted-foreground italic">Not provided</span>}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Store Display Name (Public)</Label>
                      <div className="flex items-center gap-2">
                        <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium flex-1">
                          {identity.store_name || bd.store_name || <span className="text-muted-foreground italic">Not provided</span>}
                        </div>
                        {bd.renamed_by_admin && (
                          <Badge className="bg-yellow-100 text-yellow-800">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Renamed
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Store Slug / URL</Label>
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {identity.slug || bd.store_slug || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Aniyor ID</Label>
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {vendor.aniyor_id || <span className="text-muted-foreground italic">Not assigned</span>}
                      </div>
                    </div>
                  </div>
                  
                  {/* Previous Name Info (if renamed) */}
                  {(bd.renamed_by_admin || identity.renamed_by_admin) && (
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mt-4">
                      <h4 className="font-medium text-yellow-800 flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-4 w-4" />
                        Store Renamed by Admin
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Previous Store Name:</p>
                          <p className="font-medium text-foreground">{identity.previous_store_name || bd.previous_store_name || 'Unknown'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Renamed At:</p>
                          <p className="font-medium text-foreground">{formatDateTime(identity.renamed_at || bd.renamed_at)}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-muted-foreground">Reason:</p>
                          <p className="font-medium text-foreground">{identity.renamed_reason || bd.renamed_reason || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Legal Agreements & Policy Acceptance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Legal Agreements & Audit Trail
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Naming Policy Acceptance */}
                    <div className={`p-4 rounded-lg border ${agreements.naming_policy_accepted ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium flex items-center gap-2">
                          {agreements.naming_policy_accepted ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-gray-400" />
                          )}
                          Naming Policy Acceptance
                        </h4>
                        <div className="flex items-center gap-3">
                          <Badge className={agreements.naming_policy_accepted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                            {agreements.naming_policy_accepted ? 'Accepted' : 'Not Accepted'}
                          </Badge>
                          <Switch
                            data-testid="admin-naming-policy-toggle"
                            checked={!!agreements.naming_policy_accepted}
                            disabled={namingPolicySaving}
                            onCheckedChange={handleToggleNamingPolicy}
                          />
                        </div>
                      </div>
                      
                      {agreements.naming_policy_accepted ? (
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Accepted At:</p>
                            <p className="font-medium font-mono text-xs bg-white px-2 py-1 rounded">
                              {formatDateTime(agreements.naming_policy_accepted_at)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">IP Address:</p>
                            <p className="font-medium font-mono text-xs bg-white px-2 py-1 rounded">
                              {agreements.ip_address || 'Not recorded'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-amber-700 font-medium" data-testid="naming-policy-outstanding-warning">
                          Outstanding onboarding requirement — flip the toggle above to accept on the vendor's behalf (e.g. if they missed it during onboarding).
                        </p>
                      )}
                      
                      <p className="text-xs text-gray-500 mt-3">
                        This indicates the vendor has acknowledged that store names are leased and can be changed if a trademark owner makes a claim.
                      </p>
                    </div>

                    {/* iter-325 — Admin stopgap: unblock a vendor whose
                        profile is verified complete but who is stuck on
                        the registration-gate redirect (onboarding_completed
                        flag). Separate from the Naming Policy toggle above
                        since the redirect gate checks onboarding_completed,
                        not naming policy, directly. */}
                    {!vendor.onboarding_completed && (
                      <div className="p-3 rounded-lg border border-dashed border-gray-300 bg-gray-50" data-testid="admin-force-unblock-dashboard-card">
                        <p className="text-xs text-gray-600 mb-2">
                          <strong>Admin override:</strong> if this vendor's profile is fully documented but they're stuck being redirected to registration instead of their dashboard, use this to unblock them directly (does not re-run or duplicate registration data).
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={forceUnblockSaving}
                          onClick={handleForceCompleteOnboarding}
                          data-testid="admin-force-unblock-dashboard-btn"
                        >
                          {forceUnblockSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                          Force-Unblock Dashboard Access
                        </Button>
                      </div>
                    )}

                    {/* iter-329 — Onboarding Artifact Checklist + Gate
                        Override. The Force-Unblock button above only
                        flips `onboarding_completed` — it does NOT stop
                        a vendor looping back onto onboarding routes if
                        `useOnboardingArtifacts` (frontend) independently
                        finds a missing tax/bank/pickup/subscription
                        record, since that check overrides the flag for
                        any non-'active'-status vendor. This card shows
                        the SAME 4-point check live and lets an admin
                        override it directly as an audit-logged stopgap
                        — without fabricating any missing record — while
                        the actual data gap gets root-caused separately. */}
                    {onboardingArtifacts && (
                      <div
                        className={`p-4 rounded-lg border ${onboardingArtifacts.completed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                        data-testid="admin-onboarding-artifacts-card"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium flex items-center gap-2">
                            {onboardingArtifacts.completed ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-red-500" />
                            )}
                            Onboarding Artifact Checklist
                          </h4>
                          {onboardingArtifacts.admin_onboarding_override && (
                            <Badge data-testid="admin-onboarding-override-active-badge" className="bg-amber-100 text-amber-800">
                              Admin Override Active
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                          {[
                            ['Tax Registration', onboardingArtifacts.has_tax_registration],
                            ['Pickup Address', onboardingArtifacts.has_pickup_address],
                            ['Bank Account', onboardingArtifacts.has_bank_account],
                            ['Subscription / Skipped', onboardingArtifacts.has_subscription_or_skipped],
                          ].map(([label, ok]) => (
                            <div key={label} className="flex items-center gap-2" data-testid={`onboarding-artifact-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                              {ok ? (
                                <CheckCircle className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                              ) : (
                                <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                              )}
                              <span className={ok ? 'text-gray-700' : 'text-red-700 font-medium'}>{label}</span>
                            </div>
                          ))}
                        </div>
                        {onboardingArtifacts.flag_artifact_drift && (
                          <p className="text-xs text-amber-700 mb-3" data-testid="onboarding-flag-drift-warning">
                            <strong>Flag/artifact mismatch:</strong> `onboarding_completed` is set on this
                            vendor's account, but the live artifact check above still fails — this is
                            exactly why they keep bouncing back onto onboarding routes.
                          </p>
                        )}
                        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                          <div>
                            <p className="text-sm font-medium">Override Onboarding Gate</p>
                            <p className="text-xs text-gray-500">
                              Send this vendor straight to their dashboard regardless of the checklist
                              above. Fully reversible — does not fill in any missing record.
                            </p>
                          </div>
                          <Switch
                            checked={!!onboardingArtifacts.admin_onboarding_override}
                            disabled={overrideSaving}
                            onCheckedChange={handleToggleOnboardingOverride}
                            data-testid="admin-onboarding-override-toggle"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Terms of Service & Seller Agreement */}
                    <div className={`p-4 rounded-lg border ${agreements.terms_accepted ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium flex items-center gap-2">
                          {agreements.terms_accepted ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                          )}
                          Seller Agreement & Terms of Service
                        </h4>
                        <Badge className={agreements.terms_accepted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}>
                          {agreements.terms_accepted ? 'Accepted' : 'Not Accepted'}
                        </Badge>
                      </div>
                      
                      {agreements.terms_accepted && agreements.terms_accepted_at && (
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Accepted At:</p>
                            <p className="font-medium font-mono text-xs bg-white px-2 py-1 rounded">
                              {formatDateTime(agreements.terms_accepted_at)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">IP Address:</p>
                            <p className="font-medium font-mono text-xs bg-white px-2 py-1 rounded">
                              {agreements.ip_address || 'Not recorded'}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {!agreements.terms_accepted && (
                        <p className="text-xs text-red-600 mt-2">
                          Vendor has not yet accepted the Seller Agreement. This may have been registered before the T&C requirement was enforced.
                        </p>
                      )}
                    </div>

                    {/* WhatsApp Communications Consent */}
                    <div className={`p-4 rounded-lg border ${agreements.whatsapp_communications_opt_in ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium flex items-center gap-2">
                          {agreements.whatsapp_communications_opt_in ? (
                            <CheckCircle className="h-4 w-4 text-blue-600" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-gray-400" />
                          )}
                          WhatsApp Communications
                        </h4>
                        <div className="flex items-center gap-3">
                          <Badge className={agreements.whatsapp_communications_opt_in ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}>
                            {agreements.whatsapp_communications_opt_in ? 'Opted In' : 'Not Opted In'}
                          </Badge>
                          <Switch
                            data-testid="whatsapp-optin-toggle"
                            checked={!!agreements.whatsapp_communications_opt_in}
                            disabled={whatsappOptinSaving}
                            onCheckedChange={handleToggleWhatsappOptin}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        {agreements.whatsapp_communications_opt_in 
                          ? `Consented to receive order updates, communications & offers via WhatsApp${agreements.whatsapp_communications_opt_in_at ? ` on ${formatDateTime(agreements.whatsapp_communications_opt_in_at)}` : ''}.`
                          : 'Vendor has not opted in for WhatsApp communications.'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Toggle this to opt the vendor in/out of new-order WhatsApp alerts on their behalf — same switch as the vendor's own Profile page setting.
                      </p>
                    </div>

                    {/* iter-325 — Shipping Management mode: source of truth is
                        vendor_shipping_settings.shipping_mode, same collection
                        the payout/shipping-calc engines read from. */}
                    <div
                      className={`p-4 rounded-lg border ${isSelfManagedShipping ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'}`}
                      data-testid="shipping-management-mode-card"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium flex items-center gap-2">
                          <Package className={`h-4 w-4 ${isSelfManagedShipping ? 'text-purple-600' : 'text-blue-600'}`} />
                          Shipping Management
                        </h4>
                        <Badge
                          data-testid="shipping-management-mode-badge"
                          className={isSelfManagedShipping ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}
                        >
                          {isSelfManagedShipping ? 'Self Managed' : 'Aniyor Managed'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500">
                        {isSelfManagedShipping
                          ? 'Vendor handles their own courier booking and delivery — no Aniyor shipping pool contribution applies to their payouts.'
                          : 'Aniyor books and manages delivery for this vendor — a shipping pool contribution is deducted from their payouts.'}
                      </p>
                      {!shippingSettings.shipping_mode && (
                        <p className="text-xs text-amber-600 mt-1">
                          No explicit shipping settings row found for this vendor — defaulting to Aniyor Managed.
                        </p>
                      )}
                    </div>

                    {/* Holiday Mode status — visibility only, per admin's request:
                        surfaces ON/OFF, system-lock state, reason, message and
                        return date. Read-only (no override toggle — vendors
                        control their own Holiday Mode via Settings; system-
                        imposed locks clear automatically once the underlying
                        condition — trial/subscription/payment — resolves). */}
                    <div
                      className={`p-4 rounded-lg border ${isOnHoliday ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}
                      data-testid="admin-holiday-mode-card"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium flex items-center gap-2">
                          {isOnHoliday ? (
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          Holiday Mode
                        </h4>
                        <div className="flex items-center gap-2">
                          {isOnHoliday && holidayModeLocked && (
                            <Badge data-testid="admin-holiday-mode-locked-badge" className="bg-red-100 text-red-800">
                              System Locked
                            </Badge>
                          )}
                          <Badge
                            data-testid="admin-holiday-mode-status-badge"
                            className={isOnHoliday ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}
                          >
                            {isOnHoliday ? 'ON — Store Paused' : 'OFF — Accepting Orders'}
                          </Badge>
                        </div>
                      </div>
                      {isOnHoliday ? (
                        <div className="grid grid-cols-2 gap-3 text-sm mt-2">
                          <div>
                            <p className="text-gray-600">Reason:</p>
                            <p className="font-medium text-foreground">
                              {HOLIDAY_REASON_LABELS[vendor.holiday_mode_reason] || vendor.holiday_mode_reason || 'vendor_requested'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Started At:</p>
                            <p className="font-medium text-foreground">{formatDateTime(vendor.holiday_started_at)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Expected Return:</p>
                            <p className="font-medium text-foreground">{vendor.holiday_return_date || 'Not specified'}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-gray-600">Vendor Message:</p>
                            <p className="font-medium text-foreground">{vendor.holiday_message || 'None'}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 mt-1">
                          Store is active and accepting orders normally.
                        </p>
                      )}
                      {isOnHoliday && holidayModeLocked && (
                        <p className="text-xs text-red-600 mt-2" data-testid="admin-holiday-mode-locked-note">
                          Platform-imposed pause — the vendor cannot turn this off themselves until the underlying condition ({HOLIDAY_REASON_LABELS[vendor.holiday_mode_reason] || vendor.holiday_mode_reason}) is resolved (e.g. subscription renewal).
                        </p>
                      )}
                    </div>

                    {/* Verification Badges */}
                    <div className="p-4 rounded-lg border bg-gray-50">
                      <h4 className="font-medium mb-3">Verification Badges</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className={`p-3 rounded-lg border ${verificationBadges.is_brand_owner ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <BadgeCheck className={`h-5 w-5 ${verificationBadges.is_brand_owner ? 'text-blue-600' : 'text-gray-400'}`} />
                            <span className="font-medium">Blue Tick (Brand Owner)</span>
                          </div>
                          {verificationBadges.is_brand_owner ? (
                            <p className="text-xs text-blue-700">
                              Verified: {formatDateTime(verificationBadges.brand_verified_at)}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500">Not verified</p>
                          )}
                        </div>
                        <div className={`p-3 rounded-lg border ${verificationBadges.is_legacy_store ? 'bg-green-50 border-green-200' : 'bg-white'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Store className={`h-5 w-5 ${verificationBadges.is_legacy_store ? 'text-green-600' : 'text-gray-400'}`} />
                            <span className="font-medium">Green Tick (Legacy Store)</span>
                          </div>
                          {verificationBadges.is_legacy_store ? (
                            <p className="text-xs text-green-700">
                              Verified: {formatDateTime(verificationBadges.legacy_verified_at)}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500">Not verified</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Business Info Tab */}
          <TabsContent value="business">
            <Card>
              <CardHeader>
                <CardTitle>Business Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Business Name</Label>
                    {editMode ? (
                      <Input
                        value={editData.business_name}
                        onChange={(e) => setEditData({ ...editData, business_name: e.target.value })}
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {bd.business_name || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">GST Number</Label>
                    <div className="flex gap-2">
                      {editMode ? (
                        <Input
                          value={editData.gst_number}
                          onChange={(e) => setEditData({ ...editData, gst_number: e.target.value.toUpperCase() })}
                        />
                      ) : (
                        <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium flex-1">
                          {bd.gst_number || <span className="text-muted-foreground italic">Not provided</span>}
                        </div>
                      )}
                      {vr.gst_verified ? (
                        <Badge className="bg-green-600 cursor-pointer" onClick={toggleGstVerification} title="Click to unverify">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={toggleGstVerification}
                          disabled={verifyingGst || !((editData.gst_number || bd.gst_number || '').trim())}
                          className="text-green-700 border-green-300 hover:bg-green-50"
                          data-testid="verify-gst-btn"
                        >
                          {verifyingGst ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                          Verify GST
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">PAN Number</Label>
                    {editMode ? (
                      <Input
                        value={editData.pan_number || ''}
                        onChange={(e) => setEditData({ ...editData, pan_number: e.target.value.toUpperCase() })}
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {bd.pan_number || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Enrollment ID</Label>
                    {editMode ? (
                      <Input
                        value={editData.enrollment_id}
                        onChange={(e) => setEditData({ ...editData, enrollment_id: e.target.value })}
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {bd.enrollment_id || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>

                  {/* iter-233-F — IEC Code (Import-Export Code from DGFT). Optional; presence promotes vendor to Tier-4. */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">IEC Code <span className="text-xs italic">(optional)</span></Label>
                    {editMode ? (
                      <Input
                        placeholder="Enter 10-char DGFT Import-Export Code"
                        value={editData.iec_code}
                        onChange={(e) => setEditData({ ...editData, iec_code: e.target.value.toUpperCase() })}
                        maxLength={14}
                        data-testid="vendor-iec-input"
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium" data-testid="vendor-iec-value">
                        {bd.iec_code || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                    {editMode && (
                      <p className="text-xs text-muted-foreground">
                        Required for Tier-4 (international exporter) status. Adding an IEC auto-promotes the vendor.
                      </p>
                    )}
                  </div>

                  {/* ── Business Entity Type (editable by admin) — canonical SSOT enum ── */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Company / Entity Type</Label>
                    {editMode ? (
                      <select
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                        value={editData.company_type || ''}
                        onChange={(e) => setEditData({ ...editData, company_type: e.target.value })}
                        data-testid="company-type-select"
                      >
                        <option value="">Select entity type...</option>
                        <option value="sole_proprietorship">Sole Proprietorship</option>
                        <option value="partnership">Partnership Firm</option>
                        <option value="llp">Limited Liability Partnership (LLP)</option>
                        <option value="private_limited">Private Limited Company (Pvt Ltd)</option>
                        <option value="public_limited">Public Limited Company</option>
                        <option value="opc">One Person Company (OPC)</option>
                        <option value="huf">Hindu Undivided Family (HUF)</option>
                        <option value="trust_ngo">Trust / NGO / Section 8</option>
                        <option value="foreign_entity">Foreign Entity</option>
                        <option value="other">Other</option>
                      </select>
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium" data-testid="vendor-company-type">
                        {(() => {
                          const map = {
                            sole_proprietorship: 'Sole Proprietorship',
                            partnership: 'Partnership Firm',
                            llp: 'Limited Liability Partnership (LLP)',
                            private_limited: 'Private Limited Company (Pvt Ltd)',
                            public_limited: 'Public Limited Company',
                            opc: 'One Person Company (OPC)',
                            huf: 'Hindu Undivided Family (HUF)',
                            trust_ngo: 'Trust / NGO / Section 8',
                            foreign_entity: 'Foreign Entity',
                            other: 'Other',
                          };
                          return map[bd.company_type] || bd.company_type
                            || <span className="text-muted-foreground italic">Not provided</span>;
                        })()}
                      </div>
                    )}
                  </div>

                  {/* ── Annual Turnover (editable by admin) — canonical SSOT enum ── */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Annual Turnover (₹)</Label>
                    {editMode ? (
                      <select
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                        value={editData.annual_turnover || ''}
                        onChange={(e) => setEditData({ ...editData, annual_turnover: e.target.value })}
                        data-testid="annual-turnover-select"
                      >
                        <option value="">Select turnover range...</option>
                        <option value="lt_20l">Below ₹20 Lakhs</option>
                        <option value="20l_40l">₹20 Lakhs – ₹40 Lakhs</option>
                        <option value="40l_75l">₹40 Lakhs – ₹75 Lakhs</option>
                        <option value="75l_1cr">₹75 Lakhs – ₹1 Crore</option>
                        <option value="1cr_2cr">₹1 Crore – ₹2 Crore</option>
                        <option value="2cr_5cr">₹2 Crore – ₹5 Crore</option>
                        <option value="gt_5cr">Above ₹5 Crore</option>
                      </select>
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium" data-testid="vendor-annual-turnover">
                        {(() => {
                          const map = {
                            lt_20l: 'Below ₹20 Lakhs',
                            '20l_40l': '₹20 Lakhs – ₹40 Lakhs',
                            '40l_75l': '₹40 Lakhs – ₹75 Lakhs',
                            '75l_1cr': '₹75 Lakhs – ₹1 Crore',
                            '1cr_2cr': '₹1 Crore – ₹2 Crore',
                            '2cr_5cr': '₹2 Crore – ₹5 Crore',
                            gt_5cr: 'Above ₹5 Crore',
                          };
                          return map[bd.annual_turnover] || bd.annual_turnover
                            || <span className="text-muted-foreground italic">Not provided</span>;
                        })()}
                      </div>
                    )}
                  </div>

                  {/* ── Business Nature (free text — what the vendor sells) ── */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Business Nature</Label>
                    {editMode ? (
                      <Input
                        value={editData.business_type || ''}
                        placeholder="e.g. Retail, Manufacturing, Services"
                        onChange={(e) => setEditData({ ...editData, business_type: e.target.value })}
                        data-testid="business-type-input"
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {bd.business_type || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Joined Date</Label>
                    <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                      {new Date(vendor.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Store-wide Licenses & Certifications (iter-374b) — admin
                display/edit of the same vendor-profile-level licenses the
                vendor manages on their own Store Profile page. */}
            <div className="mt-6 space-y-2">
              <div className="flex items-center justify-between">
                <div />
                {!licensesEditing ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setLicensesEditing(true)}
                    data-testid="edit-vendor-licenses-btn"
                  >
                    <Edit className="h-3.5 w-3.5 mr-1.5" /> Edit Licenses
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setLicensesEditing(false); fetchVendorLicenses(); }}
                    data-testid="cancel-edit-vendor-licenses-btn"
                  >
                    Cancel
                  </Button>
                )}
              </div>
              <StoreWideLicensesCard
                licenses={vendorLicenses}
                onAdd={addVendorLicenseRow}
                onUpdate={updateVendorLicense}
                onRemove={removeVendorLicenseRow}
                onSave={handleSaveVendorLicenses}
                saving={licensesSaving}
                uploadingIdx={uploadingLicenseIdx}
                onUploadImage={uploadVendorLicenseImage}
                onVerify={handleVerifyLicense}
                verifyingIdx={verifyingLicenseIdx}
                enforceAttemptLimit={false}
                editable={licensesEditing}
                testIdPrefix="admin-vendor-license"
                footerNote={
                  licensesLastEdited
                    ? `Last edited by ${licensesLastEdited.by} on ${new Date(licensesLastEdited.at).toLocaleString()}`
                    : null
                }
              />
            </div>
          </TabsContent>

          {/* Bank Details Tab */}
          <TabsContent value="bank">
            <Card>
              <CardHeader>
                <CardTitle>Bank Account Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Bank Name</Label>
                    {editMode ? (
                      <Input
                        value={editData.bank_name}
                        onChange={(e) => setEditData({ ...editData, bank_name: e.target.value })}
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {bk.bank_name || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Account Holder Name</Label>
                    {editMode ? (
                      <Input
                        value={editData.account_holder_name}
                        onChange={(e) => setEditData({ ...editData, account_holder_name: e.target.value })}
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {bk.account_holder_name || <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Account Number</Label>
                    {editMode ? (
                      <Input
                        value={editData.account_number}
                        onChange={(e) => setEditData({ ...editData, account_number: e.target.value })}
                      />
                    ) : (
                      <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium">
                        {bk.account_number ? `****${bk.account_number.slice(-4)}` : <span className="text-muted-foreground italic">Not provided</span>}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">IFSC Code</Label>
                    <div className="flex gap-2">
                      {editMode ? (
                        <Input
                          value={editData.ifsc_code}
                          onChange={(e) => setEditData({ ...editData, ifsc_code: e.target.value.toUpperCase() })}
                        />
                      ) : (
                        <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-medium flex-1">
                          {bk.ifsc_code || <span className="text-muted-foreground italic">Not provided</span>}
                        </div>
                      )}
                      {bk.verified && (
                        <Badge className="bg-green-600">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* iter-231-U — Admin Razorpay Route Linked Account diagnose card */}
            <div className="mt-4">
              <RazorpayRouteDiagnoseCard vendorId={vendor?.id} />
            </div>
          </TabsContent>

          {/* KYC Documents Tab */}
          <TabsContent value="kyc">
            <Card>
              <CardHeader>
                <CardTitle>KYC Documents</CardTitle>
                <CardDescription>View and manage all vendor uploaded documents — matches Vendor Profile KYC section</CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  // GST is mandatory for Tier 3 vendors
                  const vendorTier = resolvedTier;
                  const effectiveDocTypes = ADMIN_DOCUMENT_TYPES.map(d =>
                    d.id === 'gst_certificate' && (vendorTier === 'tier_3' || vendorTier === 'tier_3_full_gst') ? { ...d, required: true } : d
                  );
                  const kycDocs = vp.kyc_documents || {};
                  const requiredDocs = effectiveDocTypes.filter(d => d.required);
                  const uploadedRequired = requiredDocs.filter(d => d.legacyKeys.some(k => kycDocs[k])).length;
                  const totalUploaded = effectiveDocTypes.filter(d => d.legacyKeys.some(k => kycDocs[k])).length;
                  return (
                    <>
                {/* KYC Progress Summary */}
                    <div className="mb-6 p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-4 mb-2">
                        <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${requiredDocs.length > 0 ? (uploadedRequired / requiredDocs.length) * 100 : 0}%` }} />
                        </div>
                        <span className="text-sm font-medium text-slate-600" data-testid="kyc-progress-text">
                          {uploadedRequired} / {requiredDocs.length} Required
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">{totalUploaded} of {effectiveDocTypes.length} total documents uploaded</p>
                      {(vendorTier === 'tier_3' || vendorTier === 'tier_3_full_gst') && (
                        <p className="text-xs text-amber-700 mt-1 font-medium">Tier 3: GST Certificate is mandatory</p>
                      )}
                    </div>

                {/* Required Documents */}
                <h4 className="text-sm font-semibold text-red-600 mb-3">Required Documents</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {effectiveDocTypes.filter(d => d.required).map((docType) => {
                    const hasDoc = docType.legacyKeys.some(k => kycDocs[k]);
                    const Icon = docType.icon;
                    return (
                      <Card key={docType.id} className={`border-2 ${hasDoc ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className={`p-1.5 rounded-lg ${hasDoc ? 'bg-green-100' : 'bg-gray-100'}`}>
                                  <Icon className={`h-4 w-4 ${hasDoc ? 'text-green-600' : 'text-gray-400'}`} />
                                </div>
                                <div>
                                  <p className="font-semibold text-sm">{docType.name} <span className="text-red-500">*</span></p>
                                  <p className="text-xs text-gray-500">{docType.description}</p>
                                </div>
                              </div>
                              {hasDoc ? (
                                <Badge className="bg-green-100 text-green-800" data-testid={`${docType.id}-uploaded`}>
                                  <CheckCircle className="h-3 w-3 mr-1" /> Uploaded
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-500" data-testid={`${docType.id}-missing`}>Not Uploaded</Badge>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant={hasDoc ? 'default' : 'outline'} onClick={() => viewDocument(docType.id)} disabled={!hasDoc}
                                className={`flex-1 ${hasDoc ? 'bg-green-600 hover:bg-green-700' : ''}`} data-testid={`view-${docType.id}`}>
                                <Eye className="h-4 w-4 mr-1" /> View
                              </Button>
                              <label htmlFor={`admin-upload-${docType.id}`} className="flex-1">
                                <Button size="sm" variant="outline" className="w-full" disabled={uploadingKyc === docType.id} asChild>
                                  <span>
                                    <Upload className="h-4 w-4 mr-1" />
                                    {uploadingKyc === docType.id ? 'Uploading...' : hasDoc ? 'Replace' : 'Upload'}
                                  </span>
                                </Button>
                              </label>
                              <input id={`admin-upload-${docType.id}`} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                                onChange={(e) => handleKycUpload(docType.id, e)} />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Optional Documents */}
                <h4 className="text-sm font-semibold text-slate-600 mb-3">Optional Documents</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {effectiveDocTypes.filter(d => !d.required).map((docType) => {
                    const hasDoc = docType.legacyKeys.some(k => kycDocs[k]);
                    const Icon = docType.icon;
                    return (
                      <Card key={docType.id} className={`border-2 ${hasDoc ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className={`p-1.5 rounded-lg ${hasDoc ? 'bg-green-100' : 'bg-gray-100'}`}>
                                  <Icon className={`h-4 w-4 ${hasDoc ? 'text-green-600' : 'text-gray-400'}`} />
                                </div>
                                <div>
                                  <p className="font-semibold text-sm">{docType.name}</p>
                                  <p className="text-xs text-gray-500">{docType.description}</p>
                                </div>
                              </div>
                              {hasDoc ? (
                                <Badge className="bg-green-100 text-green-800" data-testid={`${docType.id}-uploaded`}>
                                  <CheckCircle className="h-3 w-3 mr-1" /> Uploaded
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-500" data-testid={`${docType.id}-missing`}>Not Uploaded</Badge>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant={hasDoc ? 'default' : 'outline'} onClick={() => viewDocument(docType.id)} disabled={!hasDoc}
                                className={`flex-1 ${hasDoc ? 'bg-green-600 hover:bg-green-700' : ''}`} data-testid={`view-${docType.id}`}>
                                <Eye className="h-4 w-4 mr-1" /> View
                              </Button>
                              <label htmlFor={`admin-upload-${docType.id}`} className="flex-1">
                                <Button size="sm" variant="outline" className="w-full" disabled={uploadingKyc === docType.id} asChild>
                                  <span>
                                    <Upload className="h-4 w-4 mr-1" />
                                    {uploadingKyc === docType.id ? 'Uploading...' : hasDoc ? 'Replace' : 'Upload'}
                                  </span>
                                </Button>
                              </label>
                              <input id={`admin-upload-${docType.id}`} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                                onChange={(e) => handleKycUpload(docType.id, e)} />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                    </>
                  );
                })()}

                {/* Other / Unknown documents already uploaded but not in our defined types */}
                {vp.kyc_documents && (() => {
                  const allKnownKeys = ADMIN_DOCUMENT_TYPES.flatMap(d => d.legacyKeys);
                  const extras = Object.entries(vp.kyc_documents).filter(([k, v]) => !allKnownKeys.includes(k) && v);
                  if (extras.length === 0) return null;
                  return (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold text-slate-600 mb-3">Other Uploaded Documents</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {extras.map(([key, url]) => (
                          <div key={key} className="flex items-center justify-between p-3 border rounded-lg bg-green-50/30 border-green-200">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</span>
                            </div>
                            <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700 h-7"
                              onClick={() => {
                                const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
                                const resolveUrl = (u) => {
                                  if (!u) return null;
                                  if (u.startsWith('http://') || u.startsWith('https://')) return u;
                                  if (u.startsWith('/uploads/')) return `${BACKEND_URL}/api${u}`;
                                  if (u.startsWith('/api/')) return `${BACKEND_URL}${u}`;
                                  const fn = u.replace(/^\//, '');
                                  if (!fn.includes('/')) return `${BACKEND_URL}/api/uploads/kyc_documents/${fn}`;
                                  return `${BACKEND_URL}/api/uploads/${fn}`;
                                };
                                setKycDialog({ open: true, type: key.replace(/_/g, ' ').toUpperCase(), url: resolveUrl(url) });
                              }}>
                              <Eye className="h-3 w-3 mr-1" /> View
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <div className="space-y-4">
              {(() => {
                const sub = vendor.subscription;
                return (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CreditCard className="h-5 w-5" />
                        Subscription & Tier
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Tier */}
                      <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
                        <div>
                          <p className="text-sm text-muted-foreground">Selling Capability Tier</p>
                          <p className="font-semibold text-lg">{TIER_MAP[resolvedTier] || resolvedTier}</p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {tierEditing ? (
                            <>
                              {TIER_OPTIONS.map(t => (
                                <Button key={t.value} size="sm" variant={resolvedTier === t.value ? 'default' : 'outline'}
                                  className="h-7 px-2 text-xs" disabled={savingTier}
                                  onClick={() => updateVendorTier(t.value)} data-testid={`sub-tier-select-${t.value}`}>
                                  {t.label}
                                </Button>
                              ))}
                              <Button size="sm" variant="ghost" className="h-7 px-1" onClick={() => setTierEditing(false)}>
                                <X className="h-3 w-3" />
                              </Button>
                            </>
                          ) : (
                            <Button size="sm" variant="outline" className="gap-1" onClick={() => setTierEditing(true)}>
                              <Edit className="h-3 w-3" /> Change Tier
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* iter-233 — Aniyor Subscription Plan details (SSOT-resolved).
                          Pulled lazily on tab click via
                          GET /api/vendor-subscription/admin/vendor/{id}/subscription
                          so the admin sees the SAME shape the vendor sees,
                          incl. trial extensions + drift_warnings. */}
                      {adminSubLoading && (
                        <div className="text-center py-6 text-muted-foreground text-sm">
                          Loading subscription…
                        </div>
                      )}
                      {!adminSubLoading && adminSubData?.resolved && (
                        <div className="space-y-4" data-testid="admin-subscription-detail">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 border rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Aniyor Plan</p>
                              <p className="font-semibold text-lg capitalize">
                                {adminSubData.plan?.plan_name || adminSubData.resolved.plan_name || adminSubData.resolved.plan_code || 'Unknown'}
                              </p>
                              <Badge className={`mt-1 ${
                                adminSubData.resolved.status === 'active' ? 'bg-green-100 text-green-800' :
                                adminSubData.resolved.status === 'trial'  ? 'bg-blue-100 text-blue-800' :
                                adminSubData.resolved.status === 'expired'? 'bg-red-100 text-red-800' :
                                adminSubData.resolved.status === 'error'  ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {adminSubData.resolved.status === 'trial' ? 'Trial' :
                                 adminSubData.resolved.status === 'active' ? 'Active' :
                                 adminSubData.resolved.status === 'expired' ? 'Expired' :
                                 adminSubData.resolved.status === 'error' ? 'Unresolved' :
                                 adminSubData.resolved.status === 'none' ? 'Free' :
                                 String(adminSubData.resolved.status || 'unknown')}
                              </Badge>
                            </div>
                            <div className="p-4 border rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Listing Limit</p>
                              <p className="font-semibold text-lg">
                                {(adminSubData.usage?.listings_used ?? 0)} / {adminSubData.resolved.listing_limit || '—'}
                              </p>
                              <p className="text-xs text-muted-foreground">active listings</p>
                            </div>
                            {adminSubData.resolved.trial_end && (
                              <div className="p-4 border rounded-lg">
                                <p className="text-xs text-muted-foreground mb-1">
                                  {adminSubData.resolved.trial_until_sale ? 'Trial Cap Date' : 'Trial Ends'}
                                </p>
                                <p className="font-medium text-sm">{new Date(adminSubData.resolved.trial_end).toLocaleDateString()}</p>
                                <p className="text-xs text-muted-foreground">
                                  {adminSubData.resolved.trial_until_sale
                                    ? `Until first sale/lead · 6-month cap`
                                    : `${adminSubData.resolved.trial_days_remaining ?? 0} days remaining · source: ${adminSubData.resolved.trial_end_source || '—'}`
                                  }
                                </p>
                              </div>
                            )}
                            <div className="p-4 border rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Location Limit</p>
                              <p className="font-semibold text-lg">
                                {(adminSubData.usage?.locations_used ?? 0)} / {adminSubData.resolved.location_limit || '—'}
                              </p>
                            </div>
                          </div>
                          {Array.isArray(adminSubData.resolved.drift_warnings) && adminSubData.resolved.drift_warnings.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <p className="text-xs font-semibold text-amber-900 mb-1">Drift warnings</p>
                              <ul className="text-xs text-amber-800 list-disc pl-4 space-y-0.5">
                                {adminSubData.resolved.drift_warnings.map((w, i) => <li key={i}>{w}</li>)}
                              </ul>
                            </div>
                          )}
                          {adminSubData.plan?.features && (
                            <div>
                              <p className="text-sm font-medium mb-2">Plan Features</p>
                              <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
                                {Object.entries(adminSubData.plan.features).map(([k, v]) => (
                                  <li key={k}><span className="capitalize">{k.replace(/_/g, ' ')}</span>: <span className="font-medium text-foreground">{String(v)}</span></li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      {!adminSubLoading && !adminSubData?.resolved && (
                        <div className="text-center py-8 text-muted-foreground">
                          <CreditCard className="h-10 w-10 mx-auto mb-2 opacity-30" />
                          <p>No subscription plan found</p>
                        </div>
                      )}

                      {/* ── Change Plan CTA ─────────────────────────────── */}
                      <div className="pt-2 border-t space-y-3">
                        {/* Trial Until First Sale banner */}
                        {adminSubData?.resolved?.trial_until_sale && (
                          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <span className="text-amber-600 mt-0.5 text-xs font-bold">TRIAL</span>
                            <div className="text-xs text-amber-800">
                              <span className="font-semibold">Trial Until First Sale</span> — active until the vendor records their first sale or lead, or{' '}
                              <span className="font-semibold">{adminSubData.resolved.trial_max_end ? new Date(adminSubData.resolved.trial_max_end).toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'}) : '6 months'}</span>, whichever comes first.
                            </div>
                          </div>
                        )}

                        <button
                          onClick={() => setPlanDialog(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                          data-testid="vendor-detail-change-plan-btn"
                        >
                          <CreditCard className="h-4 w-4" />
                          Change Subscription Plan
                        </button>

                        {/* Extend Until First Sale CTA */}
                        {adminSubData?.resolved?.status !== 'active' && !adminSubData?.resolved?.trial_until_sale && (
                          <button
                            data-testid="vendor-extend-until-sale-btn"
                            onClick={async () => {
                              if (!window.confirm('Extend this vendor\'s trial until their first sale or lead? This sets a 6-month maximum cap.\n\nTier-1 vendors activate on first lead. Tier-2 vendors activate on first sale or lead.')) return;
                              try {
                                const res = await apiCall(`/api/vendors/${id}/subscription/extend-until-sale`, { method: 'POST' });
                                if (res?.ok) {
                                  toast({ title: 'Trial Extended', description: res.message || 'Trial extended until first sale/lead (max 6 months)' });
                                  fetchAdminSubscription(); fetchSubHistory();
                                } else {
                                  toast({ title: 'Error', description: res?.detail || 'Failed to extend trial', variant: 'destructive' });
                                }
                              } catch (err) {
                                toast({ title: 'Error', description: err?.message || 'Failed to extend trial', variant: 'destructive' });
                              }
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            Extend Until First Sale/Lead (max 6 months)
                          </button>
                        )}

                        <p className="text-xs text-gray-400">
                          Commission rate is derived from the plan — it cannot be edited directly.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* ── Subscription History ──────────────────────────────── */}
              <Card data-testid="subscription-history-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <History className="h-4 w-4 text-gray-500" />
                    Subscription History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {subHistoryLoading ? (
                    <div className="flex items-center justify-center py-6 text-gray-400 text-sm">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading history…
                    </div>
                  ) : subHistory.length === 0 ? (
                    <div className="text-center py-6 text-gray-400 text-sm">
                      No subscription history recorded yet.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">Effective From</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">Previous Plan</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">New Plan</th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-600">Old Comm</th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-600">New Comm</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">Changed By</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-600">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subHistory.map((h, i) => (
                            <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                              <td className="px-3 py-2 font-mono text-gray-700">
                                {h.effective_from ? new Date(h.effective_from).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : '—'}
                              </td>
                              <td className="px-3 py-2 text-gray-400">
                                {h.previous_plan_name || h.previous_plan_code || '—'}
                              </td>
                              <td className="px-3 py-2 font-medium text-indigo-700">
                                {h.plan_name || h.plan_code || '—'}
                              </td>
                              <td className="px-3 py-2 text-right font-mono text-gray-400">
                                {h.previous_commission_rate != null ? `${h.previous_commission_rate}%` : '—'}
                              </td>
                              <td className="px-3 py-2 text-right font-mono font-semibold text-gray-800">
                                {h.commission_rate != null ? `${h.commission_rate}%` : '—'}
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {h.changed_by || 'System Migration'}
                              </td>
                              <td className="px-3 py-2 text-gray-400 max-w-[180px] truncate" title={h.change_reason}>
                                {h.change_reason || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* GST Portal Data Tab */}
          <TabsContent value="gst_data">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  GST Portal Data
                </CardTitle>
                <CardDescription>
                  Raw data captured from the GST government portal during verification
                </CardDescription>
              </CardHeader>
              <CardContent>
                {gstPortalLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Loading GST data...</span>
                  </div>
                ) : !gstPortalData ? (
                  <div className="text-center py-12 text-gray-500">
                    <Database className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p>No GST portal data available</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={fetchGstPortalData}>
                      <RefreshCw className="h-4 w-4 mr-2" />Fetch Data
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6" data-testid="gst-portal-data-content">
                    {/* GSTIN */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">GSTIN</Label>
                        <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm font-mono font-medium">
                          {gstPortalData.gstin || 'N/A'}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">GST Verified</Label>
                        <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm">
                          {gstPortalData.tax_registration_data?.gst_verified ? (
                            <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Tax Registration Details */}
                    {gstPortalData.tax_registration_data && (
                      <div>
                        <h3 className="text-sm font-semibold mb-3 text-gray-700 border-b pb-2">Tax Registration Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {[
                            { label: 'Legal Name', value: gstPortalData.tax_registration_data.gst_legal_name },
                            { label: 'Trade Name', value: gstPortalData.tax_registration_data.gst_trade_name },
                            { label: 'GST Status', value: gstPortalData.tax_registration_data.gst_status },
                            { label: 'Registration Date', value: gstPortalData.tax_registration_data.gst_registration_date },
                            { label: 'Constitution', value: gstPortalData.tax_registration_data.gst_constitution },
                            { label: 'Taxpayer Type', value: gstPortalData.tax_registration_data.gst_taxpayer_type },
                            { label: 'State Jurisdiction', value: gstPortalData.tax_registration_data.gst_state_jurisdiction },
                            { label: 'Center Jurisdiction', value: gstPortalData.tax_registration_data.gst_center_jurisdiction },
                            { label: 'E-Invoice Status', value: gstPortalData.tax_registration_data.gst_einvoice_status },
                            { label: 'Aadhaar Validated', value: gstPortalData.tax_registration_data.gst_aadhaar_validated },
                            { label: 'Aadhaar Validation Date', value: gstPortalData.tax_registration_data.gst_aadhaar_validation_date },
                            { label: 'eKYC Verified', value: gstPortalData.tax_registration_data.gst_ekyc_verified },
                            { label: 'Field Visit Conducted', value: gstPortalData.tax_registration_data.gst_field_visit_conducted },
                            { label: 'Compliance Tier', value: gstPortalData.tax_registration_data.compliance_tier },
                          ].map((field) => (
                            <div key={field.label} className="space-y-1">
                              <Label className="text-xs text-muted-foreground">{field.label}</Label>
                              <div className="h-9 px-3 py-2 rounded-md border bg-muted/30 text-sm truncate" title={String(field.value || 'N/A')}>
                                {field.value || <span className="text-gray-400">N/A</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Nature of Business */}
                    {gstPortalData.tax_registration_data?.gst_nature_of_business?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold mb-3 text-gray-700 border-b pb-2">Nature of Business</h3>
                        <div className="flex flex-wrap gap-2">
                          {gstPortalData.tax_registration_data.gst_nature_of_business.map((nob, i) => (
                            <Badge key={nob.id || nob.name || `${nob.title || "nob"}-${i}`} variant="outline" className="text-xs">{nob}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Principal Address */}
                    {gstPortalData.tax_registration_data?.gst_principal_address && (
                      <div>
                        <h3 className="text-sm font-semibold mb-3 text-gray-700 border-b pb-2">Principal Address (Raw)</h3>
                        <div className="bg-gray-50 rounded-lg p-4 text-sm font-mono overflow-auto max-h-48">
                          <pre className="whitespace-pre-wrap text-xs">
                            {JSON.stringify(gstPortalData.tax_registration_data.gst_principal_address, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* HSN Codes */}
                    <div>
                      <h3 className="text-sm font-semibold mb-3 text-gray-700 border-b pb-2">HSN Codes (Goods)</h3>
                      {gstPortalData.hsn_codes?.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {gstPortalData.hsn_codes.map((code, i) => (
                            <Badge key={code.id || code.name || `${code.title || "code"}-${i}`} className="bg-blue-100 text-blue-800 font-mono text-xs">{code}</Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">No HSN codes registered</p>
                      )}
                    </div>

                    {/* SAC Codes */}
                    <div>
                      <h3 className="text-sm font-semibold mb-3 text-gray-700 border-b pb-2">SAC Codes (Services)</h3>
                      {gstPortalData.sac_codes?.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {gstPortalData.sac_codes.map((code, i) => (
                            <Badge key={code.id || code.name || `${code.title || "code"}-${i}`} className="bg-purple-100 text-purple-800 font-mono text-xs">{code}</Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">No SAC codes registered</p>
                      )}
                    </div>

                    {/* Raw GST Verification Data */}
                    {gstPortalData.gst_verification && (
                      <div>
                        <h3 className="text-sm font-semibold mb-3 text-gray-700 border-b pb-2">Raw Government Portal Response</h3>
                        <div className="bg-gray-50 rounded-lg p-4 text-sm font-mono overflow-auto max-h-96">
                          <pre className="whitespace-pre-wrap text-xs">
                            {JSON.stringify(gstPortalData.gst_verification, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Credentials Tab - Super Admin Only */}
          <TabsContent value="credentials">
            <VendorCredentialsTab
              vendorId={id}
              vendorEmail={vendor?.email}
              vendorName={vendor?.name}
            />
          </TabsContent>

          {/* Pricing Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Pricing Configuration
                </CardTitle>
                <CardDescription>
                  Configure how pricing works for this vendor&apos;s products
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Manual Pricing Toggle */}
                <div className="flex items-start justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <h4 className="font-medium flex items-center gap-2">
                      Manual Pricing Mode
                    </h4>
                    <p className="text-sm text-gray-500">
                      Enable for vendors who need to confirm final pricing offline (e.g., Catering services where price depends on guest count, custom decorations, etc.)
                    </p>
                    <p className="text-xs text-gray-400">
                      When enabled, vendors can toggle between Manual/Automatic pricing per product in the Variants section.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={manualPricingEnabled}
                      onChange={(e) => setManualPricingEnabled(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className="text-sm font-medium">
                      {manualPricingEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>

                {/* Custom Message */}
                {manualPricingEnabled && (
                  <div className="space-y-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <Label className="text-amber-900">Custom Message for Customers</Label>
                    <Textarea
                      value={manualPricingMessage}
                      onChange={(e) => setManualPricingMessage(e.target.value)}
                      placeholder="e.g., Final pricing will be confirmed by the vendor based on your specific requirements."
                      rows={2}
                      className="bg-white"
                    />
                    <p className="text-xs text-amber-700">
                      This message appears on the storefront when customers select options. If left empty, a default message will be shown.
                    </p>
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button 
                    onClick={saveManualPricing}
                    disabled={savingManualPricing}
                  >
                    {savingManualPricing ? 'Saving...' : 'Save Pricing Settings'}
                  </Button>
                </div>

                {/* Info */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h5 className="font-medium text-blue-900 mb-2">How Manual Pricing Works</h5>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc pl-4">
                    <li>When enabled, the vendor sees a toggle in their product&apos;s Variants section</li>
                    <li>Products with manual pricing show &quot;Estimated&quot; prices on storefront</li>
                    <li>Customers see a warning message that final pricing will be confirmed</li>
                    <li>This is ideal for services like Catering, Event Decoration, Custom Orders</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* KYC Document Dialog */}
      <Dialog open={kycDialog.open} onOpenChange={(open) => setKycDialog({ ...kycDialog, open })}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{kycDialog.type} Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg min-h-[400px] max-h-[70vh] overflow-auto flex items-center justify-center">
              {kycDialog.url && (kycDialog.url.match(/\.(jpg|jpeg|png|webp|gif)/i)) ? (
                <>
                  <img
                    src={kycDialog.url}
                    alt={`${kycDialog.type} document`}
                    className="max-w-full max-h-[65vh] object-contain rounded-lg"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
                  />
                  <div className="text-center py-16 space-y-4 flex-col items-center" style={{ display: 'none' }}>
                    <AlertTriangle className="h-16 w-16 mx-auto text-amber-500" />
                    <p className="text-gray-700 font-medium">Document Unavailable</p>
                    <p className="text-sm text-gray-500 max-w-xs mx-auto">This document was stored on a previous server and needs to be re-uploaded.</p>
                    <label className="cursor-pointer inline-block">
                      <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => {
                        const docType = kycDialog.type?.toLowerCase().replace(/\s+/g, '_');
                        handleKycUpload(docType, e);
                        setKycDialog({ open: false, type: '', url: '' });
                      }} />
                      <span className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                        <Upload className="h-4 w-4" /> Re-upload Document
                      </span>
                    </label>
                  </div>
                </>
              ) : kycDialog.url ? (
                <div className="text-center py-16 space-y-4">
                  <FileText className="h-16 w-16 mx-auto text-blue-500" />
                  <p className="text-gray-600 font-medium">{kycDialog.type} Document</p>
                  <p className="text-sm text-gray-400">PDF and document previews open in a new tab</p>
                  <div className="flex gap-3 justify-center">
                    <a
                      href={kycDialog.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="kyc-doc-open-tab"
                    >
                      <Button>
                        <Eye className="h-4 w-4 mr-2" /> Open Document
                      </Button>
                    </a>
                    <a href={kycDialog.url} download data-testid="kyc-doc-download">
                      <Button variant="outline">
                        <Download className="h-4 w-4 mr-2" /> Download
                      </Button>
                    </a>
                  </div>
                </div>
              ) : (
                <div className="text-gray-400 text-center py-16">
                  <FileText className="h-16 w-16 mx-auto mb-4" />
                  <p>No document to preview</p>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setKycDialog({ open: false, type: '', url: '' })}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Rename Store Dialog */}
      <Dialog open={renameDialog.open} onOpenChange={(open) => !renameDialog.submitting && setRenameDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              {renameDialog.correctionType === 'correction' ? 'Correct Store Name' : 'Rename Store (Brand Change)'}
            </DialogTitle>
            <DialogDescription>
              {renameDialog.correctionType === 'correction' 
                ? 'Fix a spelling error or minor formatting issue. No redirect or credits — just a quick correction.'
                : 'Full brand name change. Creates a 90-day URL redirect and optional goodwill credits.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Correction Type Toggle */}
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg" data-testid="correction-type-toggle">
              <button
                type="button"
                onClick={() => setRenameDialog(prev => ({ ...prev, correctionType: 'correction', creditAdAmount: 0 }))}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                  renameDialog.correctionType === 'correction'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                disabled={renameDialog.submitting}
                data-testid="correction-type-quick"
              >
                Quick Correction
              </button>
              <button
                type="button"
                onClick={() => setRenameDialog(prev => ({ ...prev, correctionType: 'brand_change', creditAdAmount: 5000 }))}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                  renameDialog.correctionType === 'brand_change'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                disabled={renameDialog.submitting}
                data-testid="correction-type-brand"
              >
                Brand Name Change
              </button>
            </div>
            
            {renameDialog.correctionType === 'correction' && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-sm text-blue-800">
                Ideal for spelling fixes, capitalisation changes, or formatting corrections during early onboarding. No URL redirect or ad credits will be applied.
              </div>
            )}
            
            {/* Current Store Name */}
            <div className="bg-gray-50 p-3 rounded-lg text-sm">
              <p className="text-gray-600">Current Store Name:</p>
              <p className="font-medium">
                {identity.store_name || bd.store_name || vendor?.name || 'N/A'}
              </p>
            </div>
            
            {/* New Store Name */}
            <div className="space-y-2">
              <Label htmlFor="new-store-name">New Store Name *</Label>
              <Input
                id="new-store-name"
                placeholder="Enter corrected store name"
                value={renameDialog.newStoreName}
                onChange={(e) => setRenameDialog(prev => ({ ...prev, newStoreName: e.target.value }))}
                disabled={renameDialog.submitting}
                data-testid="new-store-name-input"
              />
              <p className="text-xs text-gray-500">Must be 3-50 characters and unique</p>
            </div>
            
            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="rename-reason">Reason *</Label>
              <Textarea
                id="rename-reason"
                placeholder={renameDialog.correctionType === 'correction' 
                  ? "E.g., Fixing spelling error, Correcting capitalisation" 
                  : "E.g., Trademark claim by brand owner, Policy violation"}
                value={renameDialog.reason}
                onChange={(e) => setRenameDialog(prev => ({ ...prev, reason: e.target.value }))}
                disabled={renameDialog.submitting}
                rows={2}
                data-testid="rename-reason-input"
              />
              <p className="text-xs text-gray-500">
                Minimum {renameDialog.correctionType === 'correction' ? '5' : '10'} characters.
                {renameDialog.correctionType === 'brand_change' && ' This will be shared with the vendor.'}
              </p>
            </div>
            
            {/* Options — only show full options for brand_change */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Send Notification Email</Label>
                  <p className="text-xs text-gray-500">Notify vendor about the name change</p>
                </div>
                <input
                  type="checkbox"
                  checked={renameDialog.sendNotification}
                  onChange={(e) => setRenameDialog(prev => ({ ...prev, sendNotification: e.target.checked }))}
                  disabled={renameDialog.submitting}
                  className="h-4 w-4 rounded border-gray-300"
                  data-testid="send-notification-checkbox"
                />
              </div>
              
              {renameDialog.correctionType === 'brand_change' && (
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Goodwill Ad Credits (₹)</Label>
                    <p className="text-xs text-gray-500">Credit vendor&apos;s ad wallet as compensation</p>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    step="500"
                    value={renameDialog.creditAdAmount}
                    onChange={(e) => setRenameDialog(prev => ({ ...prev, creditAdAmount: parseFloat(e.target.value) || 0 }))}
                    disabled={renameDialog.submitting}
                    className="w-28 text-right"
                    data-testid="credit-amount-input"
                  />
                </div>
              )}
            </div>
            
            {/* Warning — only for brand_change */}
            {renameDialog.correctionType === 'brand_change' && (
              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 text-sm">
                <p className="text-yellow-800 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Important:</strong> This action creates a 90-day URL redirect, credits the vendor&apos;s ad wallet, and is logged in the audit trail.
                  </span>
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRenameDialog(prev => ({ ...prev, open: false }))}
              disabled={renameDialog.submitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRenameStore}
              disabled={renameDialog.submitting || !renameDialog.newStoreName || !renameDialog.reason}
              data-testid="rename-store-submit"
            >
              {renameDialog.submitting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {renameDialog.correctionType === 'correction' ? 'Correcting...' : 'Renaming...'}
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {renameDialog.correctionType === 'correction' ? 'Correct Name' : 'Rename Store'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Vendor Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !deleteDialog.deleting && setDeleteDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Permanently Delete Vendor
            </DialogTitle>
            <DialogDescription className="text-left space-y-3 pt-2">
              <p>
                You are about to <strong>permanently delete</strong> this vendor account:
              </p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{vendor?.name}</p>
                <p className="text-sm text-gray-500">{vendor?.email}</p>
                <Badge variant={vendor?.status === 'active' ? 'default' : 'secondary'} className="mt-1">
                  {vendor?.status}
                </Badge>
              </div>
              <p className="text-red-600 font-medium">
                This action is IRREVERSIBLE and will delete:
              </p>
              <ul className="text-sm list-disc list-inside space-y-1 text-gray-600">
                <li>Vendor user account</li>
                <li>All listings</li>
                <li>Tax registration data</li>
                <li>Bank account details</li>
                <li>Media files references</li>
                <li>All notifications</li>
              </ul>
              <p className="text-sm text-gray-500 mt-2">
                Orders will be preserved for accounting but marked as vendor deleted.
              </p>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <Label htmlFor="confirm-delete" className="text-sm font-medium">
              Type <span className="font-mono bg-gray-100 px-1 rounded">DELETE</span> to confirm:
            </Label>
            <Input
              id="confirm-delete"
              value={deleteDialog.confirmText}
              onChange={(e) => setDeleteDialog(prev => ({ ...prev, confirmText: e.target.value.toUpperCase() }))}
              placeholder="Type DELETE"
              className={deleteDialog.confirmText === 'DELETE' ? 'border-red-500' : ''}
              disabled={deleteDialog.deleting}
            />
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, confirmText: '', deleting: false })}
              disabled={deleteDialog.deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteVendor}
              disabled={deleteDialog.confirmText !== 'DELETE' || deleteDialog.deleting}
            >
              {deleteDialog.deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Permanently
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════ GST CAPTCHA Verification Modal ═══════ */}
      <Dialog open={gstModal.open} onOpenChange={(open) => !gstModal.submitting && setGstModal(s => ({ ...s, open }))}>
        <DialogContent className="max-w-md" data-testid="gst-captcha-modal">
          <DialogHeader>
            <DialogTitle>Verify GST via Portal</DialogTitle>
            <DialogDescription>
              Solve the CAPTCHA from the Indian GST portal to fetch live business details for
              <span className="font-mono font-bold ml-1">{(gstModal.gstin || editData.gst_number || '').toUpperCase()}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="rounded-md border bg-muted/30 p-3 flex items-center justify-center min-h-[80px]">
              {gstModal.loading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : gstModal.captchaImage ? (
                <img src={gstModal.captchaImage} alt="GST CAPTCHA" className="h-16" data-testid="gst-captcha-image" />
              ) : (
                <span className="text-xs text-muted-foreground">CAPTCHA not loaded</span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Enter CAPTCHA text"
                value={gstModal.captchaAnswer}
                onChange={(e) => setGstModal(s => ({ ...s, captchaAnswer: e.target.value, error: '' }))}
                disabled={gstModal.submitting || gstModal.loading}
                autoFocus
                data-testid="gst-captcha-input"
              />
              <Button
                variant="outline"
                onClick={refreshCaptcha}
                disabled={gstModal.submitting || gstModal.loading}
                title="Refresh CAPTCHA"
                data-testid="gst-captcha-refresh"
              >
                ↻
              </Button>
            </div>
            {gstModal.error && (
              <div className="text-sm text-red-600 bg-red-50 rounded p-2" data-testid="gst-captcha-error">
                {gstModal.error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setGstModal({ open: false, loading: false, sessionId: '', captchaImage: '', captchaAnswer: '', submitting: false, error: '' })}
              disabled={gstModal.submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={submitGstCaptcha}
              disabled={gstModal.submitting || gstModal.loading || !gstModal.captchaAnswer.trim() || !gstModal.sessionId}
              data-testid="gst-captcha-submit"
            >
              {gstModal.submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying...</>
              ) : 'Verify with GST Portal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Change Plan Dialog (single vendor) ─────────────────────────── */}
      <Dialog open={planDialog} onOpenChange={(o) => { setPlanDialog(o); if (!o) { setPlanDialogCode(''); setPlanDialogReason(''); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="vendor-detail-plan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-indigo-600" />
              Change Subscription Plan
            </DialogTitle>
            <DialogDescription>
              Commission rate is locked to the plan — it cannot be overridden.
              A subscription history entry will be created with your admin email as "Changed By."
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 gap-2">
              {PLAN_OPTIONS.map(plan => (
                <button key={plan.code} type="button" onClick={() => setPlanDialogCode(plan.code)}
                  data-testid={`vendor-detail-plan-${plan.code}`}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg border-2 text-left transition-all ${
                    planDialogCode === plan.code ? 'border-indigo-500 bg-indigo-50 text-indigo-900' : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${planDialogCode === plan.code ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'}`}>
                      {planDialogCode === plan.code && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <span className="font-semibold">{plan.label}</span>
                  </div>
                  <span className={`text-sm font-mono font-bold px-2 py-0.5 rounded ${planDialogCode === plan.code ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
                    {plan.commission}%
                  </span>
                </button>
              ))}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Reason <span className="text-gray-400 font-normal">(optional)</span></label>
              <input type="text" placeholder="e.g. Correcting incorrect plan assignment"
                value={planDialogReason} onChange={e => setPlanDialogReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                data-testid="vendor-detail-plan-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialog(false)}>Cancel</Button>
            <Button onClick={handleSinglePlanChange} disabled={!planDialogCode || planSaving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="vendor-detail-plan-confirm-btn">
              {planSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
              {planSaving ? 'Saving…' : 'Update Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default VendorDetailsNew;
